// ./src/core/gameengine/BaseGameController.ts

import { GlobalBus } from '@core/Bus/EventBus';
import { normalizeOutgoingChatMessage } from '@core/gameengine/chat/chatMessageLimits';
import type { IAudio } from '@core/Audio/IAudio';
import {
  pauseMusicForTransport,
  restoreMusicAfterTransport,
} from '@core/Audio/audioBus';
import { OutboundService } from '@core/Web/services/OutboundService';
import type { PixiCanvas } from '@core/Canvas/PixiCanvas';
import type { AssetManager } from '@core/Assets';
import type { AssetService } from '@core/Assets';
import { Container } from 'pixi.js';
import React from 'react';
import type { ProvablyFairUIStyles } from '@core/UI/styles/provablyFairUiStyles';
import type { AutoPlayModalStyleResolver } from '@core/UI/styles/AutoPlay.styles';
import {
  DEFAULT_GAME_BET_LIMITS,
  type GameBetLimits,
} from '@core/gameengine/config/GameBetLimits';
import { createRoot } from 'react-dom/client';
import { AutoPlayService } from '@core/gameengine/services/AutoPlayService';
import { ScreenWakeLockManager } from '@core/utils/screenWakeLock';
import {
  useGamingSessionStore,
  tryStartFreshGamingSession,
  clearGamingSessionOnHide,
  trackSessionBalance,
  reconcileReconnectBalance,
} from '@core/regulatory';
import {
  StompMessage,
  isStompTick,
  isStompError,
  isStompRoundSummary,
  isStompSnapshot,
  StompError,
  PlaceBetPayload,
  CashoutPayload,
  CancelBetPayload,
  isStompGenericAck,
  StompGenericAck,
  isStompJoinAck,
  StompJoinAck,
  isStompPong,
  PingPayload,
  PongResponse,
  isStompAllActiveRounds,
  isStompActiveBetPlayers,
  isStompTotalCashoutAmount,
  isStompActiveVsTotalBetsCount,
  isStompRoundUpdate,
  isStompChatHistory,
  isStompChatLikeUpdate,
  isStompChatMessage,
  isStompAck,
  StompAck,
  CashoutAck,
  PlaceBetAck,
  isStompPhaseUpdate,
  StompPhaseUpdate,
  StompRoundUpdate,
} from '@core/Web/types/StompMessage';
import { STOMP_CONFIG } from '@Web/config/StompConfig';
import {
  CONNECTION_CONFIG,
  CONNECTION_FAILURE_REASON,
} from '@Web/config/ConnectionConfig';
import {
  isTickWatchdogStale,
  recordPongMiss,
  recordPongReceived,
  resolveJoinHandshakeAction,
  resolveVisibilityResumeAction,
  shouldAcceptMessageBeforeJoin,
  shouldDispatchReconnect,
  shouldDropStaleGeneration,
  shouldFailBootstrapTimeout,
} from '@Web/config/connectionHealth';
import { PhaseManager } from '@core/gameengine/state/phases/PhaseManager';
import { WaitingPhase } from '@core/gameengine/state/phases/WaitingPhase';
import type { BetSide, BetBoxState, Phase } from '@platform/contracts';
import { ThemeProvider } from '@core/UI/context/ThemeContext';
import { AuthService } from '@core/Web/services/AuthService';
import { applyServerNicknameFromPayload } from '@core/gameengine/state/auth';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { SKYX_NEW_NS } from '@core/utils/skyxNewI18n';
import i18next from '@core/i18n';
import { formatCentsToCurrency } from '@core/utils/CurrencyFormatter';
import { parseEventTimestampMs } from '@core/utils/parseTimestamp';
import { BetLockPhase } from '@core/gameengine/state/phases/BetLockPhase';
import { TakeOffPhase } from '@core/gameengine/state/phases/TakeOffPhase';
import { FlewAwayPhase } from '@core/gameengine/state/phases/FlewAwayPhase';
import { FlyingPhase } from '@core/gameengine/state/phases/FlyingPhase';
import { RevealPhase } from '@core/gameengine/state/phases/RevealPhase';
import { PHASE_CONFIG } from '@core/gameengine/config/PhaseConfig';
import { ClientSeedService } from '@core/utils/ClientSeedGenerator';
import { UserService } from '@core/Web/services/userService';
import { getGameFromUrl } from '@core/utils/urlParser';
import { logCrashReconnect } from '@core/gameengine/reconnect/crashReconnectLog';
import { logConnection } from '@core/gameengine/reconnect/connectionLog';
import {
  applyReconnectSnapshot,
  mapServerPhaseToClient,
  parseJoinSnapshot,
  validateSnapshotIdentity,
  type CrashReconnectSnapshot,
} from '@core/gameengine/reconnect/crashReconnectSnapshot';
import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';
import {
  registerActiveGameStore,
  unregisterActiveGameStore,
} from '@core/gameengine/state/activeGameStore';
import {
  registerGameAssets,
  unregisterGameAssets,
} from '@core/Assets/assetManifestRegistry';
import { track } from '@core/analytics';

export interface GameDeps {
  stage: PixiCanvas;
  assetManager: AssetManager;
  audioPlayer: IAudio;
  assetService: AssetService;
  outboundService: OutboundService;
  /** Public `/games/<folder>/` directory; defaults to route slug when omitted. */
  assetBaseSlug?: string;
}

export interface HistoryChipColors {
  /** bust &lt; 2x */
  low: string;
  /** 2x ≤ bust &lt; 10x */
  medium: string;
  /** bust ≥ 10x */
  high: string;
}

/** Multiplier text colors inside My Bets History modal rows. */
export interface BetHistoryMultiplierColors {
  low: string;
  medium: string;
  high: string;
  /** Inclusive start of medium tier (default 10). */
  mediumMin?: number;
  /** Values above this use high tier (default 20). */
  highMin?: number;
}

export function resolveBetHistoryMultiplierColor(
  multiplier: number,
  config: BetHistoryMultiplierColors
): string {
  const mediumMin = config.mediumMin ?? 10;
  const highMin = config.highMin ?? 20;
  if (multiplier > highMin) return config.high;
  if (multiplier >= mediumMin) return config.medium;
  return config.low;
}

export interface MainBetButtonChromeState {
  background: string;
  border: string;
  /** Brighter gradient on desktop hover (no outer glow). */
  hoverBackground?: string;
  hoverBorder?: string;
  /** Inset bevel only â€” avoid outer spread shadows that read as glow. */
  boxShadow?: string;
  hoverBoxShadow?: string;
  pressedBoxShadow?: string;
}

export function resolveMainBetChromeButtonStyle(
  variant: MainBetButtonChromeState,
  hovered: boolean,
  pressed: boolean
): Pick<
  React.CSSProperties,
  'background' | 'border' | 'boxShadow' | 'filter' | 'transition'
> {
  const transition =
    'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease';

  if (pressed) {
    return {
      background: variant.background,
      border: 'none',
      boxShadow: variant.pressedBoxShadow ?? 'inset 0 2px 4px rgba(0,0,0,0.28)',
      filter: 'none',
      transition,
    };
  }

  if (hovered) {
    return {
      background: variant.hoverBackground ?? variant.background,
      border: variant.hoverBorder ?? variant.border,
      boxShadow:
        variant.hoverBoxShadow ??
        variant.boxShadow ??
        'inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.18)',
      filter: 'brightness(1.08)',
      transition,
    };
  }

  return {
    background: variant.background,
    border: variant.border,
    boxShadow:
      variant.boxShadow ??
      'inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.18)',
    filter: 'none',
    transition,
  };
}

export interface MainBetButtonChrome {
  bet: MainBetButtonChromeState;
  waiting: MainBetButtonChromeState;
  cashout: MainBetButtonChromeState;
}

export interface AutoCashoutToggleChrome {
  /** Toggle track when auto cash out is off. */
  trackOff: string;
  /** Toggle track when auto cash out is on. */
  trackOn: string;
  /** Optional thumb color when off (defaults to theme grey). */
  thumbOff?: string;
  /** Optional thumb color when on (defaults to theme grey). */
  thumbOn?: string;
  /** Optional outer glow when on (box-shadow). */
  trackOnGlow?: string;
}

export interface BetPanelAutoPlayButtonChrome {
  off: {
    background: string;
    border: string;
    color: string;
    boxShadow?: string;
  };
  on: {
    background: string;
    border: string;
    color: string;
    boxShadow?: string;
  };
}

export interface BetPanelAutoCashoutInputChrome {
  disabled: {
    background: string;
    border: string;
    color: string;
    opacity: number;
  };
  enabled: {
    background: string;
    border: string;
    color: string;
  };
  focus: {
    border: string;
    boxShadow: string;
  };
  multiplierColorDisabled?: string;
  multiplierColorEnabled?: string;
}

export interface BetPanelAutoControlsChrome {
  autoPlayButton: BetPanelAutoPlayButtonChrome;
  autoCashoutToggle: AutoCashoutToggleChrome;
  autoCashoutLabel: {
    off: string;
    on: string;
  };
  autoCashoutInput: BetPanelAutoCashoutInputChrome;
}

export interface BetPanelQuickBetChipChrome {
  background: string;
  border: string;
  color: string;
  borderRadius: string;
  boxShadow?: string;
}

export interface BetPanelStakeStepperChrome {
  background: string;
  color: string;
  disabledBackground: string;
  disabledColor: string;
}

export interface BetPanelStakeInputChrome {
  background: string;
  color: string;
  border: string;
  focusBorder: string;
  focusBoxShadow: string;
  nonZeroBorder: string;
  nonZeroBoxShadow: string;
}

export interface AutoPlayModalBehaviorConfig {
  roundOptions?: number[];
  defaultRounds?: number;
  hideStopToggles?: boolean;
  defaultStopAmount?: number;
  /** Minimum stop amount (default 0 — minus disabled at 0.00). */
  minStopAmount?: number;
  /** Maximum stop amount; when unset, player balance is used. */
  maxStopAmount?: number;
  /** Max for “stop on loss” (cash decrease); falls back to maxStopAmount / balance. */
  maxStopOnCashDecrease?: number;
  /** Max for “stop on profit” (cash increase); falls back to maxStopAmount / balance. */
  maxStopOnCashIncrease?: number;
  /** Max for “stop on single win”; falls back to maxStopAmount / balance. */
  maxStopOnSingleWin?: number;
  /** When true, user can start with rounds only (stop fields at 0). */
  allowRoundsOnlyStart?: boolean;
  /** Walk a discrete list for stop +/- (skyx_new). */
  useStopAmountSteps?: boolean;
  /** Base stop list before high-value doubling (skyx_new). */
  stopAmountBaseValues?: readonly number[];
}

export type BetPanelInputVisual = {
  background: string;
  border: string;
  color: string;
  opacity: number;
  boxShadow?: string;
};

export function resolveBetPanelInputVisual(
  chrome: BetPanelAutoCashoutInputChrome,
  isEnabled: boolean
): BetPanelInputVisual {
  if (!isEnabled) {
    return {
      background: chrome.disabled.background,
      border: chrome.disabled.border,
      color: chrome.disabled.color,
      opacity: chrome.disabled.opacity,
    };
  }

  return {
    background: chrome.enabled.background,
    border: chrome.focus.border,
    color: chrome.enabled.color,
    opacity: 1,
    boxShadow: chrome.focus.boxShadow,
  };
}

export interface BetPanelAutoPlayLayout {
  text?: Partial<
    Record<
      'desktop' | 'mobile' | 'tablet',
      Pick<React.CSSProperties, 'top' | 'left'>
    >
  >;
  button?: Partial<
    Record<'desktop' | 'mobile' | 'tablet', React.CSSProperties>
  >;
  /**
   * Auto Cash Out row alignment when Auto Play is hidden (e.g. UKGC).
   * Applied from platform UI config — not hardcoded in BetBox.
   */
  autoCashout?: {
    /** Center the ACO row in the Auto tab. */
    center?: boolean;
    /** Relative `left` offset for the toggle+label cluster (device keyed). */
    toggleLeft?: Partial<
      Record<'desktop' | 'mobile' | 'tablet', string | number>
    >;
  };
}

export type HistoryHandheldLayoutEntry = {
  wrapHeight?: string;
  wrapPadding?: string;
  wrapBorderRadius?: string;
  toggleTop?: string;
  toggleRight?: string;
  expandedTop?: string;
  expandedMargin?: string;
  fadeWidth?: string;
};

export type HistoryHandheldLayoutConfig = {
  portrait: HistoryHandheldLayoutEntry;
  landscape: HistoryHandheldLayoutEntry;
};

export type ProvablyFairHandheldLandscapeConfig = {
  overlay: React.CSSProperties;
  modal?: React.CSSProperties;
  contentArea?: React.CSSProperties;
};

export type ProvablyFairRoundSectionChrome = {
  colors: {
    server: string;
    client: string;
    hash: string;
    result: string;
  };
  styleSectionTitle: (
    base: React.CSSProperties,
    color: string
  ) => React.CSSProperties;
  styleSectionIcon: (
    base: React.CSSProperties,
    filter?: string
  ) => React.CSSProperties;
  renderGameResultIcon?: (size?: number) => React.ReactNode;
};

export interface GamePlatformUIConfig {
  profile?: string;
  headerLogoSrc?: string;
  headerContainerStyle?: React.CSSProperties;
  balanceAmountStyle?: React.CSSProperties;
  /** Gap between balance/currency and menu button in the header (px). */
  headerBalanceMenuGapPx?: number;
  /** In-game header logo height per device (px). SkyX New — edit in GameConfig.ts. */
  headerLogoHeightPx?: Partial<Record<'desktop' | 'tablet' | 'mobile', number>>;
  /** Menu player-row avatar diameter (px). */
  menuPlayerAvatarSizePx?: number;
  scoreboardStyleOverrides?: Record<string, React.CSSProperties>;
  /** My Bets History modal style overrides (skyx_new). */
  betHistoryStyleOverrides?: Record<string, React.CSSProperties>;
  /** Multiplier column colors in My Bets History modal (skyx_new). */
  betHistoryMultiplierColors?: BetHistoryMultiplierColors;
  /** Show start/end balance columns in My Bets History (regulatory, e.g. Spain). */
  betHistoryShowBalances?: boolean;
  betHistoryModalCss?: string;
  /** Provably Fair round modal style overrides (skyx_new). */
  provablyFairStyleOverrides?: Partial<ProvablyFairUIStyles>;
  /** Handheld landscape PF round modal — keeps header/close below game header. */
  resolveProvablyFairHandheldLandscape?: (
    deviceType: string,
    isLandscape: boolean
  ) => boolean;
  provablyFairHandheldLandscapeConfig?: ProvablyFairHandheldLandscapeConfig;
  /** Real PF round audit + verify (game provides API-backed flow). */
  provablyFairRealMode?: boolean;
  /** i18n game id for Provably Fair copy (e.g. skyx_new). */
  provablyFairI18nGameId?: string;
  /** PF info modal header icon URL. */
  resolveProvablyFairInfoHeaderIconUrl?: (
    baseUrl?: string
  ) => string | undefined;
  /** PF "What is Provably Fair" flow diagram. */
  renderProvablyFairInfoDiagram?: (ctx: {
    assetManager?: AssetManager;
  }) => React.ReactNode;
  /** PF round modal section title/icon accents. */
  provablyFairRoundSectionChrome?: ProvablyFairRoundSectionChrome;
  /** Main bet panel button gradients/borders (skyx_new). */
  mainBetButtonChrome?: MainBetButtonChrome;
  /** Auto cash out toggle track/thumb colors (skyx_new). */
  autoCashoutToggleChrome?: AutoCashoutToggleChrome;
  /** Auto Play button, auto cash out toggle/label/input chrome (skyx_new). */
  betPanelAutoControlsChrome?: BetPanelAutoControlsChrome;
  /** Remove outer bet-panel chrome borders (skyx_new). */
  betPanelBorderless?: boolean;
  betPanelQuickBetChipChrome?: BetPanelQuickBetChipChrome;
  betPanelStakeStepperChrome?: BetPanelStakeStepperChrome;
  betPanelStakeInputChrome?: BetPanelStakeInputChrome;
  betPanelButtonPressCss?: string;
  /** Text colors for multiplier history chips (below header). */
  historyChipColors?: HistoryChipColors;
  /** Optional overrides for the history bar container (e.g. border color). */
  historyWrapStyle?: React.CSSProperties;
  /** Handheld history bar layout profile (portrait / landscape), game-provided. */
  historyHandheldLayout?: HistoryHandheldLayoutConfig;
  /** Picks portrait vs landscape entry from historyHandheldLayout. */
  resolveHistoryHandheldLayout?: (
    deviceType: 'desktop' | 'tablet' | 'mobile',
    isLandscape: boolean
  ) => HistoryHandheldLayoutEntry | null;
  /** Autoplay modal styles (skyx_new palette; layout matches core). */
  resolveAutoPlayModalStyles?: AutoPlayModalStyleResolver;
  /** Autoplay modal rounds/stop-field behavior (skyx_new). */
  autoPlayModalBehavior?: AutoPlayModalBehaviorConfig;
  /** Auto Play button label layout in bet panel (skyx_new). */
  betPanelAutoPlayLayout?: BetPanelAutoPlayLayout;
  /** Mobile/tablet bet panel layout (skyx_new â€” desktop styles unchanged). */
  resolveBetPanelHandheldStyles?: (
    base: Record<string, React.CSSProperties>,
    deviceType: 'desktop' | 'tablet' | 'mobile',
    isLandscape?: boolean
  ) => Record<string, React.CSSProperties>;
  /** Desktop bet panel stake row layout (skyx_new — mobile/tablet unchanged). */
  resolveBetPanelDesktopStyles?: (
    base: Record<string, React.CSSProperties>
  ) => Record<string, React.CSSProperties>;
  /** Handheld bet panel heights when inline compactPurple heights apply (skyx_new). */
  betPanelHandheldHeights?: { bet: string; auto: string };
  /** Desktop bet panel heights when inline compactPurple heights apply (skyx_new). */
  betPanelDesktopHeights?: { bet: string; auto: string };
  /** Play button_click via document listener; skip duplicate bet SFX in controller (skyx_new). */
  useGlobalButtonClickSound?: boolean;
  /** Loop SFX key while holding stake +/- (registered in game sound map). */
  stakeCounterLoopSound?: string;
}

export abstract class BaseGameController {
  protected outbound: OutboundService;
  protected audio: IAudio;
  protected deps!: GameDeps;
  private unsubscribeFunctions: (() => void)[] = [];
  private chatAppSubscribeUnsub: (() => void) | null = null;
  protected root = new Container();
  protected reactRoot?: any;
  protected phaseManager!: PhaseManager;
  protected autoPlayService!: AutoPlayService;
  private readonly autoplayWakeLock = new ScreenWakeLockManager();
  private heartbeatIntervalTimer: ReturnType<typeof setInterval> | null = null;
  private waitingForPong: boolean = false;
  private lastPingTimestamp: number = 0;
  private missedPongCount = 0;
  private consecutiveTickerFailures = 0;
  private static readonly MAX_CONSECUTIVE_TICKER_FAILURES = 5;
  private joinAckTimeout: ReturnType<typeof setTimeout> | null = null;
  private connectionBootstrapTimeout: ReturnType<typeof setTimeout> | null =
    null;
  private joinRetryCount = 0;
  private stompTopicUnsubs: (() => void)[] = [];
  private realtimeReady = false;
  private activeConnectionGeneration = 0;
  private reconnectRequestInProgress = false;
  private lastRealtimeEventAt = 0;
  private lastTickWatchdogCheckAt = 0;
  private pongTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private isBgMusicPlaying = false;
  private cashoutRetries: Map<BetSide, number> = new Map();
  /**
   * Maps betNo → side for in-flight CASHOUT_NOW requests.
   * Survives FLEW_AWAY wiping bet slots so a late OK ACK still shows the popup.
   */
  private pendingCashouts: Map<number, BetSide> = new Map();
  private processedCashoutAckBetNos = new Set<number>();
  protected suppressAutoplayRoundSounds: boolean = false;
  private hasTrackedIntroClosed = false;
  private registeredAssetSlugs: string[] = [];
  protected assetBaseSlug = '';

  constructor(outbound: OutboundService, audio: IAudio) {
    this.outbound = outbound;
    this.audio = audio;
    this.autoPlayService = new AutoPlayService(this);
  }

  initialize(deps: GameDeps): void {
    this.deps = deps;
    this.assetBaseSlug = deps.assetBaseSlug ?? this.getGameSlug();
    registerActiveGameStore(this.getGameStateStore());
    const manifest = this.getAssetManifest();
    if (manifest) {
      const avatarResolver = this.getPlayerAvatarUrl?.bind(this);
      const slugs = new Set<string>([
        this.getGameSlug(),
        this.getAssetBaseSlug(),
      ]);
      const routeSlug = getGameFromUrl();
      if (routeSlug) {
        slugs.add(routeSlug);
      }
      this.registeredAssetSlugs = [];
      for (const slug of slugs) {
        registerGameAssets(slug, manifest, avatarResolver);
        this.registeredAssetSlugs.push(slug);
      }
    } else {
      this.registeredAssetSlugs = [];
    }
    this.root.eventMode = 'static';
    this.phaseManager = new PhaseManager({
      manager: this.phaseManager,
      store: this.getGameStateStore(), // Use dynamic store from game controller
      audio: this.audio,
      controller: this,
    });
    this.phaseManager['context'].manager = this.phaseManager;
    this.phaseManager.transitionTo(
      new WaitingPhase(this.phaseManager['context'])
    );
  }

  public getAssetManager(): AssetManager {
    if (!this.deps || !this.deps.assetManager) {
      throw new Error('AssetManager is not initialized in GameDeps.');
    }
    return this.deps.assetManager;
  }

  public getAudio(): IAudio {
    return this.audio;
  }

  // Abstract method to get the game state store (zustand store)
  abstract getGameStateStore(): any;
  // Abstract method to get current game state
  abstract getGameState(): any;
  // Abstract method to get game slug (e.g., 'crash', 'aviator')
  abstract getGameSlug(): string;
  abstract getBetSides(): BetSide[];
  abstract getUIComponent(): React.ComponentType<any>;
  abstract createVisualComponents(): Promise<void>;
  abstract updateVisuals(deltaTime: number): void;

  getAssetManifest(): any | null {
    return null;
  }

  getPlayerAvatarUrl?(_playerId: string): string {
    return '';
  }

  getPlatformUIConfig(): GamePlatformUIConfig {
    return {};
  }

  /** Bet / button-click SFX from bet actions (skipped when global button listener is active). */
  protected playBetActionSound(): void {
    if (!this.audio || this.getPlatformUIConfig()?.useGlobalButtonClickSound) {
      return;
    }
    this.audio.play('bet');
  }

  /** Betting limits from the game store config (`GameConfig.betLimits`). */
  getBetLimits(): GameBetLimits {
    const limits = this.getGameState()?.betLimits;
    return limits ?? DEFAULT_GAME_BET_LIMITS;
  }

  /** Folder under `public/games/` for static assets (may differ from route slug). */
  getAssetBaseSlug(): string {
    if (this.assetBaseSlug) {
      return this.assetBaseSlug;
    }
    return resolveAssetFolderSlug(this.getGameSlug()) || this.getGameSlug();
  }

  /**
   * Get asset URL for a given asset path.
   * This method uses the game slug to construct the correct asset path.
   */
  getAssetUrl(assetPath: string): string {
    if (!assetPath) {
      return '';
    }
    if (/^(https?:\/\/|data:image\/|blob:)/i.test(assetPath)) {
      return assetPath;
    }
    const assetManager = this.getAssetManager();
    return buildGamePublicAssetUrl(
      this.getAssetBaseSlug(),
      assetPath,
      assetManager?.baseUrl
    );
  }

  /**
   * Triggers the flew away animation.
   * Base implementation is empty and intended to be overridden.
   * @param durationMs The duration the animation should take, in milliseconds.
   */
  public startFlewAwayAnimation(durationMs: number): void {
    console.log(
      `[BaseGameController] startFlewAwayAnimation called with duration: ${durationMs}ms`
    ); // Base implementation does nothing.
  }

  /**
   * Fetches the balance via REST API immediately.
   * This ensures the UI is populated before the Socket connection is fully established.
   */
  /**
   * Fetches the balance via REST API immediately.
   */
  protected async fetchInitialBalance(): Promise<void> {
    try {
      // You can either pass the token explicitly or let UserService fetch it internally.
      // Since we are inside the game loop, passing it explicitly is often safer for race conditions.
      const token = AuthService.getInstance().getAccessToken();

      if (!token) return;

      console.log('[Balance] Fetching initial balance via REST API...');

      // --- UPDATED CALL HERE ---
      const { balance, currency } =
        await UserService.getInstance().getBalance(token);
      // ------------------------

      if (currency) {
        this.getGameState().setCurrency(currency);
      }

      // Convert cents to main currency unit
      const formattedBalance = formatCentsToCurrency(balance);

      if (!isNaN(formattedBalance)) {
        this.getGameState().setBalance(formattedBalance);
        useGamingSessionStore
          .getState()
          .ensureSessionStartBalance(formattedBalance);
        console.log(
          `[Balance] Synced from API: ${formattedBalance} ${currency}`
        );
      }
    } catch (error) {
      console.error('[Balance] Failed to sync initial balance via API:', error);
    }
  }

  async mount(_container: HTMLDivElement): Promise<void> {
    if (!this.deps || !this.phaseManager) {
      throw new Error('Controller not initialized. Call initialize() first.');
    }

    // --- NEW: Sync balance immediately on mount ---
    this.fetchInitialBalance();
    // ----------------------------------------------

    this.getGameState().setConnectionStatus('connecting');
    this.scheduleConnectionBootstrapTimeout();
    void this.bootstrapConnection();

    const unsubscribeSocketStatus = GlobalBus.on(
      'socketio:status',
      (status: string) => {
        switch (status) {
          case 'connected':
            this.handleSocketConnected();
            break;
          case 'reconnecting':
            this.handleSocketReconnecting();
            break;
          case 'disconnected':
            this.handleSocketDisconnected();
            break;
          case 'error':
            this.requestReconnect(CONNECTION_FAILURE_REASON.STOMP_ERROR);
            break;
          case 'failed':
            this.handleSocketFailed();
            break;
        }
      }
    );
    this.unsubscribeFunctions.push(unsubscribeSocketStatus);

    // UKGC: keep lastKnownBalance in sync on every wallet update.
    let previousTrackedBalance = this.getGameState().balance;
    const unsubscribeBalanceTrack = this.getGameStateStore().subscribe(
      (state: { balance?: number }) => {
        const next = state.balance;
        if (typeof next !== 'number' || next === previousTrackedBalance) {
          return;
        }
        previousTrackedBalance = next;
        trackSessionBalance(next);
      }
    );
    this.unsubscribeFunctions.push(unsubscribeBalanceTrack);

    if (this.outbound.getAdaptor()?.getConnected()) {
      console.log(
        '[GameController] Socket already connected, triggering join...'
      );
      GlobalBus.emit('socketio:status', 'connected');
    }

    if (typeof document !== 'undefined') {
      const handleVisibilityResume = () => {
        this.handleVisibilityResume();
      };
      document.addEventListener('visibilitychange', handleVisibilityResume);
      this.unsubscribeFunctions.push(() =>
        document.removeEventListener('visibilitychange', handleVisibilityResume)
      );
    }

    const gameStateStore = this.getGameStateStore();
    let previousAutoPlaySettings = gameStateStore.getState().autoPlaySettings;
    const unsubscribeAutoPlay = gameStateStore.subscribe((state: any) => {
      const settings = state.autoPlaySettings;
      if (settings !== previousAutoPlaySettings) {
        // Check if any side has autoplay enabled
        const hasActiveAutoplay = settings.A !== null || settings.B !== null;
        const hadActiveAutoplay =
          previousAutoPlaySettings.A !== null ||
          previousAutoPlaySettings.B !== null;

        if (hasActiveAutoplay && !hadActiveAutoplay) {
          console.log(
            '[GameController] Starting autoplay with settings:',
            settings
          );

          // Play a single "start" sound for the user action.
          if (this.audio && !this.suppressAutoplayRoundSounds) {
            this.playBetActionSound();
          }

          // Start the service AFTER the click sound (so sound is not suppressed).
          this.autoPlayService.start(settings);
          this.autoplayWakeLock.setEnabled(true);

          // Now suppress per-round bet sounds produced by autoplay.
          this.suppressAutoplayRoundSounds = true;
        } else if (!hasActiveAutoplay && hadActiveAutoplay) {
          if (this.autoPlayService.isRunning()) {
            console.log('[GameController] Stopping autoplay');
            this.autoPlayService.stop();
          }
          this.autoplayWakeLock.setEnabled(false);

          // Re-enable bet sounds for manual actions when autoplay stops
          this.suppressAutoplayRoundSounds = false;
        }
        previousAutoPlaySettings = settings;
      }
    });

    this.unsubscribeFunctions.push(unsubscribeAutoPlay);

    await this.createVisualComponents();
    this.deps.stage.root.addChild(this.root);

    const hudMount = document.getElementById('gamehud');
    if (!hudMount) throw new Error('Could not find element with id "gamehud"');
    this.reactRoot = this.reactRoot || createRoot(hudMount);
    this.reactRoot.render(
      React.createElement(
        ThemeProvider,
        null,
        React.createElement(this.getUIComponent(), {
          controller: this,
          audioManager: this.audio,
        })
      )
    );
    this.deps.stage.app.ticker.add(this.update, this);
  }

  async unmount(): Promise<void> {
    for (const slug of this.registeredAssetSlugs) {
      unregisterGameAssets(slug);
    }
    this.registeredAssetSlugs = [];
    unregisterActiveGameStore();
    this.clearJoinHandshakeTimeout();
    this.clearConnectionBootstrapTimeout();
    this.stopHeartbeat();
    this.realtimeReady = false;
    this.unsubscribeStompTopics();
    if (this.chatAppSubscribeUnsub) {
      this.chatAppSubscribeUnsub();
      this.chatAppSubscribeUnsub = null;
    }
    this.unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    this.unsubscribeFunctions = [];
    this.autoplayWakeLock.dispose();

    this.outbound.disconnect();
    this.reactRoot?.unmount();
    this.deps.stage.app.ticker.remove(this.update, this);
    if (this.isBgMusicPlaying) {
      this.audio.stopMusic();
      this.isBgMusicPlaying = false;
    }
    //Dispose audio service to clean up Howl instances
    this.audio.dispose();
    this.root.destroy({ children: true });
  }

  private update = (): void => {
    try {
      this.checkRealtimeStale();
      const deltaTime = this.deps.stage.app.ticker.deltaMS;
      this.updateVisuals(deltaTime);
      this.phaseManager.update(deltaTime);
      this.autoPlayService.update();
      this.consecutiveTickerFailures = 0;
    } catch (err) {
      this.consecutiveTickerFailures++;

      // Enhanced error logging with game state context
      const gameState = this.getGameState();
      console.error(
        `[BaseGameController] Ticker update failed (${this.consecutiveTickerFailures} consecutive):`,
        {
          error: err,
          phase: gameState.phase,
          nowX: gameState.nowX,
          historyCount: gameState.history?.length,
          notificationsCount: gameState.notifications?.length,
          allBetsCount: gameState.allBets?.length,
          stack: err instanceof Error ? err.stack : undefined,
        }
      );

      // A persistent (non-transient) error would otherwise retry every frame
      // forever, spamming ~60 console/telemetry errors/sec. Stop the loop and
      // reuse the existing "connection failed" reload overlay instead.
      if (
        this.consecutiveTickerFailures >=
        BaseGameController.MAX_CONSECUTIVE_TICKER_FAILURES
      ) {
        console.error(
          '[BaseGameController] Too many consecutive ticker failures, stopping render loop.'
        );
        this.deps.stage.app.ticker.remove(this.update, this);
        this.getGameState().setConnectionStatus('failed');
        this.outbound.stopAutoReconnect();
      }
    }
  };

  protected processCashout(
    side: BetSide,
    cashoutMultiplier: number,
    winAmount: number,
    newBalance: number,
    showNotification: boolean = true
  ): void {
    const betState = this.getGameState().bets[side];
    const isAutoCashout = betState.autoCashoutEnabled;

    // Only mutate the bet slot when it still belongs to this cashout attempt.
    // A very late ACK after WAITING reset may find the side via pendingCashouts
    // while the slot is already idle/armed for the next round — still show toast.
    const canUpdateBetSlot =
      betState.status === 'cashing' ||
      betState.status === 'placed' ||
      betState.status === 'cashed';

    if (canUpdateBetSlot && betState.status !== 'cashed') {
      track('cashout', {
        value: winAmount,
        multiplier: cashoutMultiplier,
        side,
      });
      track('win', {
        value: winAmount,
        multiplier: cashoutMultiplier,
        bet_amount: betState.stake,
        side,
      });
    }

    if (canUpdateBetSlot) {
      const patch: Partial<BetBoxState> = {
        status: 'cashed',
        cashedAtX: cashoutMultiplier,
        win: winAmount,
        error: '', // Clear any previous errors on successful cashout
      };

      // --- FIX START: Only reset Auto Cashout to 1.1 if we are NOT in Auto Play mode ---
      // If we are in Auto Play (betMode === 'auto'), we must preserve the user's setting (e.g., 1.20)
      // so the next round uses the correct value.
      if (isAutoCashout && betState.betMode !== 'auto') {
        patch.autoCashoutX = 1.1;
      }
      // --- FIX END ---

      this.getGameState().upBet(side, patch);
    } else {
      console.log(
        `[Controller] Late cashout ACK for ${side} after slot reset (status=${betState.status}) — applying balance/toast only.`
      );
    }

    const stakeForNet = betState.stake;
    if (stakeForNet > 0) {
      useGamingSessionStore
        .getState()
        .noteBetCashout(side, stakeForNet, winAmount);
    }

    if (betState.betNo != null) {
      this.pendingCashouts.delete(betState.betNo);
    }

    if (!isNaN(newBalance)) {
      this.getGameState().setBalance(newBalance);
      console.log(
        `[Controller] Balance updated to: ${newBalance} from cashout ack.`
      );
    }
    // Play cashout sound - can be overridden in child classes
    this.playCashoutSound(side, cashoutMultiplier);
    if (showNotification) {
      this.getGameState().addNotification({
        type: 'success',
        multiplier: cashoutMultiplier,
        winAmount: winAmount,
        betSide: side,
      });
    }
    // --- MODIFIED: Call new function in AutoPlayService ---
    this.autoPlayService.checkStopConditionsOnCashout(side, winAmount);
  }

  protected applyUnmappedCashoutAck(
    cashoutMultiplier: number,
    winAmount: number,
    newBalance: number,
    betNo?: number
  ): void {
    const bets = this.getGameState().bets;
    let side =
      this.resolveCashoutSide(betNo) ??
      this.getBetSides().find(s => {
        const st = bets[s]?.status;
        return st === 'placed' || st === 'cashing' || st === 'cashed';
      });

    if (side) {
      this.processCashout(side, cashoutMultiplier, winAmount, newBalance, true);
      return;
    }

    if (!isNaN(newBalance)) {
      this.getGameState().setBalance(newBalance);
      console.log(
        `[Controller] Balance updated to: ${newBalance} from unmapped cashout ack.`
      );
    }

    this.playCashoutSound('A', cashoutMultiplier);
    this.getGameState().addNotification({
      type: 'success',
      multiplier: cashoutMultiplier,
      winAmount,
    });
  }

  protected syncGamingSessionCashedBetsFromReconnect(): void {
    const session = useGamingSessionStore.getState();
    if (!session.policy.sessionDisplays) return;
    if (session.sessionStartedAtMs == null || session.gameSessionId == null) {
      return;
    }

    const bets = this.getGameState().bets;
    for (const side of this.getBetSides()) {
      const bet = bets[side];
      if (bet?.status !== 'cashed') continue;
      const stake = bet.stake;
      const payout = bet.win;
      if (
        !(stake > 0) ||
        typeof payout !== 'number' ||
        !Number.isFinite(payout)
      ) {
        continue;
      }

      const pendingStake = session.pendingRoundStakes[side];
      if (pendingStake == null || pendingStake <= 0) continue;
      if (typeof session.pendingRoundPayouts[side] === 'number') continue;

      session.noteBetCashout(side, stake, payout);
    }
  }

  private clearProcessedCashoutAcks(): void {
    this.processedCashoutAckBetNos.clear();
  }

  private finalizeCashoutAck(
    betNo: number | undefined,
    side: BetSide | undefined,
    cashoutMultiplier: number,
    winAmount: number,
    newBalance: number
  ): void {
    const isDuplicate =
      betNo != null && this.processedCashoutAckBetNos.has(betNo);

    if (isDuplicate) {
      console.log(
        `[Controller] Duplicate cashout ACK for betNo ${betNo} â€” skipping popup.`
      );
      if (betNo != null) {
        this.pendingCashouts.delete(betNo);
      }
      if (!isNaN(newBalance)) {
        this.getGameState().setBalance(newBalance);
      }
      return;
    }

    if (betNo != null) {
      this.processedCashoutAckBetNos.add(betNo);
      this.pendingCashouts.delete(betNo);
    }

    if (side) {
      this.processCashout(side, cashoutMultiplier, winAmount, newBalance, true);
      return;
    }

    console.warn(
      `[Controller] Cashout ACK for unknown bet slot ${betNo} â€” showing popup anyway.`
    );
    this.applyUnmappedCashoutAck(
      cashoutMultiplier,
      winAmount,
      newBalance,
      betNo
    );
  }

  // Can be overridden in child classes for custom cashout sounds
  protected playCashoutSound(_side: BetSide, _cashoutMultiplier: number): void {
    if (this.audio) {
      this.audio.play('cashout');
    }
  }

  // Can be overridden in child classes for custom crash sounds
  public playCrashSound(): void {
    if (this.audio) {
      this.audio.play('crash');
    }
  }

  private handleServerMessage = (payload: StompMessage): void => {
    const generation = this.outbound.getConnectionGeneration();
    if (
      shouldDropStaleGeneration(this.activeConnectionGeneration, generation)
    ) {
      return;
    }

    const isChat =
      isStompChatHistory(payload) ||
      isStompChatMessage(payload) ||
      isStompChatLikeUpdate(payload);
    if (
      !this.realtimeReady &&
      !shouldAcceptMessageBeforeJoin({
        isPong: isStompPong(payload),
        isJoinAck: isStompJoinAck(payload),
        isError: isStompError(payload),
        isChat,
      })
    ) {
      return;
    }

    if (!isStompPong(payload) && !isChat) {
      this.lastRealtimeEventAt = Date.now();
    }

    if (isStompPong(payload)) {
      this.handlePong(payload);
      return;
    }
    if (isStompAck(payload)) {
      this.handleDirectAck(payload);
      return;
    }
    if (isStompPhaseUpdate(payload)) {
      this.handlePhaseUpdate(payload);
    } else if (isStompRoundSummary(payload)) {
      console.log('📝 [ROUND_SUMMARY] Received summary:', payload);
      const gameSlug = this.getGameSlug();
      console.log(
        `📝 [ROUND_SUMMARY] Adding to history for game: ${gameSlug}`,
        {
          roundId: payload.roundNo,
          bust: payload.bust,
        }
      );
      this.getGameState().addHistory({
        roundId: payload.roundNo,
        bust: payload.bust,
        serverSeed: payload.serverSeed || 'N/A',
        serverSeedHash: payload.serverSeedHash || 'N/A',
        timeStamp:
          parseEventTimestampMs(payload.ts ?? payload.endedAt) ?? Date.now(),
      });
      if (payload.roundNo != null) {
        this.getGameState().setRound(String(payload.roundNo));
        this.setCurrentRoundNo(payload.roundNo);
      }
      console.log(
        `📝 [ROUND_SUMMARY] History after add:`,
        this.getGameState().history.length,
        'items'
      );
    } else if (isStompTick(payload) || isStompSnapshot(payload)) {
      if (isStompTick(payload) && payload.roundId != null) {
        this.getGameState().setRound(String(payload.roundId));
      } else if (
        isStompSnapshot(payload) &&
        payload.p?.round?.roundId != null
      ) {
        this.getGameState().setRound(String(payload.p.round.roundId));
      }
      this.phaseManager.handleMessage(payload);
    } else if (isStompGenericAck(payload)) {
      // Check if this is a cashout-related ACK for debug logging
      const isCashoutAck = payload.p?.data?.result?.payout !== undefined;
      if (isCashoutAck) {
        console.log('🔔 [current-debug] CASHOUT GENERIC ACK RECEIVED:', {
          fullPayload: payload,
          payout: payload.p?.data?.result?.payout,
          atX: payload.p?.data?.result?.atX,
          roundId: payload.p?.data?.result?.roundId,
          betNo: payload.p?.data?.result?.betNo,
          balance: payload.p?.data?.result?.balance,
          currency: payload.p?.data?.result?.currency,
          timestamp: Date.now(),
        });
      } else {
        console.log(
          '🔔 [ACK] Received Generic ACK:',
          JSON.stringify(payload, null, 2)
        );
      }
      this.handleAck(payload);
    } else if (isStompJoinAck(payload)) {
      this.handleJoinAck(payload);
    } else if (isStompError(payload)) {
      this.handleErrorMessage(payload.p);
    } else if (isStompAllActiveRounds(payload)) {
      this.handleAllActiveRounds(payload);
    } else if (isStompActiveBetPlayers(payload)) {
      this.handleActiveBetPlayers(payload);
    } else if (isStompTotalCashoutAmount(payload)) {
      this.handleTotalCashoutAmount(payload);
    } else if (isStompActiveVsTotalBetsCount(payload)) {
      this.handleActiveVsTotalBetsCount(payload);
    } else if (isStompRoundUpdate(payload)) {
      this.handleRoundUpdate(payload);
    } else if (isStompChatHistory(payload)) {
      this.handleChatHistory(payload);
    } else if (isStompChatLikeUpdate(payload)) {
      this.handleChatLikeUpdate(payload);
    } else if (isStompChatMessage(payload)) {
      this.handleChatMessage(payload);
    } else {
      console.warn('Unhandled message type:', payload);
    }
  };

  private handlePhaseUpdate(payload: StompPhaseUpdate): void {
    const { phase, phaseStart, phaseEnd, roundId } = payload.p;
    const incomingRoundNo = this.parsePositiveRoundNo(payload.p.roundNo);
    const state = this.getGameState();
    const prevRoundId =
      state.roundId != null && String(state.roundId).length > 0
        ? String(state.roundId)
        : null;
    const prevRoundNo = this.parsePositiveRoundNo(state.roundNo);

    console.log(`[Phase] Received PHASE_UPDATE: ${phase}`);
    if (roundId != null && Number.isFinite(Number(roundId))) {
      state.setRound(String(roundId));
      // Mint a fresh PF client seed for the new round (unique per game/round).
      this.resolveClientSeed(String(roundId));
    }

    const isNewRoundStart =
      phase === 'LOBBY' &&
      prevRoundId != null &&
      roundId != null &&
      String(roundId) !== prevRoundId;

    if (incomingRoundNo != null) {
      this.setCurrentRoundNo(incomingRoundNo);
    } else if (isNewRoundStart && prevRoundNo != null) {
      // ROUND_UPDATE at round end locked the finished round; advance so
      // previous bets can open PF once the next round is live.
      this.setCurrentRoundNo(prevRoundNo + 1);
    }
    const context = this.phaseManager['context'];
    // Calculate duration from server timestamps if available, otherwise use default.
    let duration = PHASE_CONFIG[phase as Phase]?.defaultDuration ?? 0;
    if (phaseStart && phaseEnd && phaseEnd > phaseStart) {
      duration = phaseEnd - phaseStart;
      console.log(
        `[Phase] Server-defined duration for ${phase}: ${duration}ms`
      );
    } else {
      console.log(`[Phase] Using default duration for ${phase}: ${duration}ms`);
    }
    // MODIFIED: Set both total and remaining duration.
    // When a new phase starts, remaining duration IS the total duration.
    this.getGameState().setPhaseDurations(duration, duration);
    // Create timing data object for phase transitions
    const timingData = {
      phase,
      phaseStart,
      phaseEnd,
    };
    // Transition to the new phase, passing server timing data for logging.
    // The server sends "LOBBY" for the waiting phase.
    switch (phase) {
      case 'LOBBY':
        this.clearProcessedCashoutAcks();
        this.phaseManager.transitionTo(new WaitingPhase(context), timingData);
        break;
      case 'BET_LOCK':
        this.phaseManager.transitionTo(new BetLockPhase(context), timingData);
        break;
      case 'TAKEOFF':
        this.phaseManager.transitionTo(new TakeOffPhase(context), timingData);
        break;
      case 'FLEW_AWAY':
        // MODIFIED: Pass the calculated duration to the phase constructor
        this.phaseManager.transitionTo(
          new FlewAwayPhase(context, payload.p, duration),
          timingData
        );
        break;
      case 'REVEAL':
        this.phaseManager.transitionTo(
          new RevealPhase(context, payload.p), // Now passes bust value
          timingData
        );
        break;
      default:
        console.warn(`[Phase] Unknown phase received: ${phase}`);
    }
  }

  private handleDirectAck(ack: StompAck): void {
    console.log('✅ [ACK-DIRECT] Processing Direct ACK:', {
      request: ack.request,
      status: ack.status,
      data: ack.data,
    });
    if (ack.request === 'PLACE_BET' && ack.status === 'OK') {
      const data = ack.data as PlaceBetAck['data'];
      console.log('🎯 [ACK-BET] PLACE_BET ACK received - betNo:', data.betNo);
      const side = this.byBetNo(data.betNo);
      if (!side) {
        console.error(
          '❌ [ACK-BET] Cannot find side for betNo:',
          data.betNo,
          '- Current bets:',
          this.getGameState().bets
        );
        return;
      }
      console.log(`✅ [ACK-BET] Bet placed successfully for side ${side}`, {
        betNo: data.betNo,
        betId: data.betId,
        balance: data.balance,
        currentStatus: this.getGameState().bets[side].status,
      });
      this.getGameState().upBet(side, {
        status: 'placed',
        serverBetId: data.betId,
        placedAtX: this.getGameState().nowX,
        error: '', // Clear any previous errors
      });
      console.log(`📊 [ACK-BET] Updated side ${side} status to "placed"`);
      const placedStake = this.getGameState().bets[side].stake;
      if (placedStake > 0) {
        useGamingSessionStore.getState().noteBetPlaced(side, placedStake);
      }
      const newBalance = formatCentsToCurrency(data.balance);
      if (!isNaN(newBalance)) {
        this.getGameState().setBalance(newBalance);
        console.log(`💰 [ACK-BET] Balance updated to: ${newBalance}`);
      }

      const gameState = this.getGameState();
      if (
        gameState.bets[side].betMode === 'auto' &&
        gameState.autoPlaySettings[side] !== null
      ) {
        const betStake = gameState.bets[side].stake;
        const progress = gameState.autoPlayProgress[side];
        if (progress && betStake) {
          const newNetProfitLoss = progress.netProfitLoss - betStake;
          gameState.updateAutoPlayProgress(side, {
            roundsPlayed: progress.roundsPlayed + 1,
            netProfitLoss: newNetProfitLoss,
          });
          console.log(
            `[AutoPlayService] Side ${side}: Incremented roundsPlayed to ${
              progress.roundsPlayed + 1
            } on successful bet placement`
          );
        }
      }
    } else if (ack.request === 'CASHOUT' && ack.status === 'OK') {
      const data = ack.data as CashoutAck['data'];
      const sideFromSlot = this.byBetNo(data.betNo);
      const side = this.resolveCashoutSide(data.betNo);
      console.log('💰 [current-debug] CASHOUT RESPONSE RECEIVED:', {
        fullAck: ack,
        cashoutData: data,
        betNo: data.betNo,
        side,
        payoutAtX: data.atX,
        payout: data.payout,
        newBalance: data.balance,
        timestamp: Date.now(),
        resolvedFromPending: !sideFromSlot && !!side,
      });
      const winAmount = formatCentsToCurrency(data.payout);
      const newBalance = formatCentsToCurrency(data.balance);
      this.finalizeCashoutAck(
        data.betNo,
        side,
        data.atX,
        winAmount,
        newBalance
      );
    } else if (ack.request === 'CASHOUT' && ack.status !== 'OK') {
      const betNo = (ack.data as any)?.betNo as number | undefined;

      let side = this.resolveCashoutSide(betNo);
      if (!side) {
        const gameState = this.getGameState();
        side = Object.keys(gameState.bets).find(
          s => gameState.bets[s as BetSide].status === 'cashing'
        ) as BetSide | undefined;
      }

      this.getGameState().addNotification({
        type: 'error',
        message: this.getStageTimedOutMessage(),
      });

      if (side) {
        this.applyCashoutFailure(side, this.getStageTimedOutMessage(), betNo);
      }
    } else if (
      (ack.request === 'CANCEL' || ack.request === 'CANCEL_BET') &&
      ack.status === 'OK'
    ) {
      const data = ack.data as {
        balance: number;
        betNo?: number;
        [key: string]: any;
      };
      console.log('🔄 [ACK-CANCEL] CANCEL_BET ACK received:', data);
      // Find the side that was cancelled and change status to idle
      const side = this.byBetNo(data.betNo);
      if (side) {
        console.log(
          `[debugBet] Cancel confirmed by server - changing side ${side} to idle`
        );
        this.getGameState().upBet(side, {
          status: 'idle',
          error: '',
          serverBetId: undefined,
          betNo: undefined,
        });

        // --- MODIFIED: REMOVED manual removal of player bet.
        // We let the ALL_ACTIVE_ROUNDS message handle the list update to avoid clearing valid bets.
        // if (data.betNo) {
        //   this.getGameState().removePlayerBet(
        //     data.betNo,
        //     this.getGameState().bets[side].stake
        //   );
        // }
      }
      const newBalance = formatCentsToCurrency(data.balance);
      if (!isNaN(newBalance)) {
        this.getGameState().setBalance(newBalance);
        console.log(`💰 [ACK-CANCEL] Balance updated to: ${newBalance}`);
      }
    } else if (
      // Handle CANCEL errors, especially CANCEL_STAGE_TIMEOUT during BET_LOCK
      (ack.request === 'CANCEL' || ack.request === 'CANCEL_BET') &&
      ack.status !== 'OK'
    ) {
      const currentPhase = this.getGameState().phase;
      const errorMessage =
        (ack.data as any)?.message ||
        (ack as any).message ||
        'Cancel request failed';

      const isCancelTimeout =
        /timed out, cannot cancel bet|cancellation not allowed during takeoff/i.test(
          String(errorMessage)
        );

      console.log('❌ [ACK-CANCEL-ERROR] Cancel request failed:', {
        request: ack.request,
        status: ack.status,
        message: errorMessage,
        currentPhase,
        data: ack.data,
      });

      console.log(`[debugBet] handleDirectAck processing cancel error:`, {
        request: ack.request,
        status: ack.status,
        currentPhase,
        errorMessage,
        data: ack.data,
      });

      // Find the side that was being cancelled and revert status
      // Try to find by betNo first, if not available, find by status 'cancelling'
      let side = this.byBetNo((ack.data as any)?.betNo);
      if (!side) {
        // If betNo not available in error response, find the side with 'cancelling' status
        const gameState = this.getGameState();
        const cancellingSide = Object.keys(gameState.bets).find(
          s => gameState.bets[s as BetSide].status === 'cancelling'
        ) as BetSide | undefined;
        side = cancellingSide;
        console.log(
          `[debugBet] betNo not found in error response, found cancelling side: ${side}`
        );
      }

      if (side) {
        console.log(
          `[debugBet] Cancel rejected by server - reverting side ${side} to placed`
        );
        this.getGameState().upBet(side, {
          status: 'placed',
        });
      }

      // If server sent a known timeout message, show generic "Stage timeout" popup
      if (isCancelTimeout) {
        this.getGameState().addNotification({
          type: 'error',
          message: this.getStageTimedOutMessage(),
        });
        return;
      }

      // Special handling for CANCEL_STAGE_TIMEOUT during BET_LOCK phase
      if (
        currentPhase === 'BET_LOCK' &&
        errorMessage.includes('CANCEL_STAGE_TIMEOUT')
      ) {
        console.log(
          `[debugBet] 🔒 CANCEL_STAGE_TIMEOUT during BET_LOCK - keeping bet locked, not changing status`
        );
        // Don't change the bet status during BET_LOCK phase when cancel times out
        // The bet should remain 'placed' and locked in
        // Just show the error notification
        this.getGameState().addNotification({
          type: 'error',
          message: this.getStageTimedOutMessage(),
        });
        return;
      }
      // For other cancel errors, do not show a user-facing error
    } else if (
      // Handle PLACE_BET errors, especially STAGE_TIMEOUT during BET_LOCK
      ack.request === 'PLACE_BET' &&
      ack.status !== 'OK'
    ) {
      const currentPhase = this.getGameState().phase;
      const errorMessage =
        (ack.data as any)?.message ||
        (ack as any).message ||
        'Place bet request failed';

      console.log('❌ [ACK-PLACE-ERROR] Place bet request failed:', {
        request: ack.request,
        status: ack.status,
        message: errorMessage,
        currentPhase,
        data: ack.data,
      });

      console.log(`[debugBet] handleDirectAck processing place bet error:`, {
        request: ack.request,
        status: ack.status,
        currentPhase,
        errorMessage,
        data: ack.data,
      });

      // Find the side that was placing the bet and revert status
      // Try to find by betNo first, if not available, find by status 'placing'
      let side = this.byBetNo((ack.data as any)?.betNo);
      if (!side) {
        // If betNo not available in error response, find the side with 'placing' status
        const gameState = this.getGameState();
        const placingSide = Object.keys(gameState.bets).find(
          s => gameState.bets[s as BetSide].status === 'placing'
        ) as BetSide | undefined;
        side = placingSide;
        console.log(
          `[debugBet] betNo not found in place bet error response, found placing side: ${side}`
        );
      }

      if (side) {
        console.log(
          `[debugBet] Place bet rejected by server - reverting side ${side} to idle`
        );
        this.getGameState().upBet(side, {
          status: 'idle',
          error: this.getStageTimedOutMessage(),
          serverBetId: undefined,
          betNo: undefined,
        });
      } else {
        console.log(
          `[debugBet] Could not find side to revert place bet error - betNo: ${
            (ack.data as any)?.betNo
          }`
        );
      }

      // Special handling for STAGE_TIMEOUT during BET_LOCK phase
      if (
        currentPhase === 'BET_LOCK' &&
        errorMessage.includes('STAGE_TIMEOUT')
      ) {
        console.log(
          `[debugBet] 🔒 STAGE_TIMEOUT during BET_LOCK - bet placement rejected, reverting to idle`
        );
        // Bet placement was rejected due to stage timeout, revert to idle
        // This is already handled above, just show notification
        this.getGameState().addNotification({
          type: 'error',
          message: this.getStageTimedOutMessage(),
        });
        return;
      }
      // For any place bet errors, show a generic timeout message
      this.getGameState().addNotification({
        type: 'error',
        message: this.getStageTimedOutMessage(),
      });
    } else {
      console.log(
        'Received a direct ACK that is not a cashout or placebet, or has an error status:',
        ack
      );
    }
  }

  private handleSocketConnected(): void {
    console.log(
      '[GameController] WebSocket connected, setting up subscriptions...'
    );
    logConnection('CONNECTED');
    this.joinRetryCount = 0;
    this.realtimeReady = false;
    this.activeConnectionGeneration = this.outbound.getConnectionGeneration();
    this.reconnectRequestInProgress = false;
    const currentGameSlug = getGameFromUrl();
    if (currentGameSlug === 'chickenluck') {
      console.log(
        '[GameController] ChickenLuck game skipping the Core Stomp Connection.'
      );
    } else {
      try {
        this.subscribeAll();
        logCrashReconnect('subscriptions created', {
          generation: this.activeConnectionGeneration,
        });
        this.startHeartbeat();
      } catch (error) {
        console.error(
          '[GameController] Failed to subscribe to STOMP topics:',
          error
        );
        this.requestReconnect(CONNECTION_FAILURE_REASON.SOCKET_ERROR);
        return;
      }
    }
    this.join();
    this.scheduleJoinHandshake();
    // Resume BGM from persisted Music preference (do not leave a stale runtime mute).
    restoreMusicAfterTransport(this.audio);
  }

  protected async bootstrapConnection(): Promise<void> {
    try {
      await this.outbound.connect();
    } catch (error) {
      console.error('[GameController] STOMP connect failed:', error);
    }
  }

  private scheduleConnectionBootstrapTimeout(): void {
    this.clearConnectionBootstrapTimeout();
    this.connectionBootstrapTimeout = setTimeout(() => {
      const status = this.getGameState().connectionStatus;
      if (!shouldFailBootstrapTimeout(status)) {
        return;
      }
      console.error(
        '[GameController] Connection bootstrap timed out while status was:',
        status
      );
      this.getGameState().setConnectionStatus('failed');
      this.outbound.stopAutoReconnect();
    }, CONNECTION_CONFIG.connection.bootstrapTimeoutMs);
  }

  private clearConnectionBootstrapTimeout(): void {
    if (this.connectionBootstrapTimeout) {
      clearTimeout(this.connectionBootstrapTimeout);
      this.connectionBootstrapTimeout = null;
    }
  }

  private handleSocketReconnecting(): void {
    this.realtimeReady = false;
    this.stopHeartbeat();
    this.clearJoinHandshakeTimeout();
    if (this.getGameState().connectionStatus === 'failed') {
      return;
    }
    this.getGameState().setConnectionStatus('reconnecting');
    clearGamingSessionOnHide('socket_reconnecting');
    // Pause playback only — never flip Music preference / Menu state.
    pauseMusicForTransport(this.audio);
    this.onTransportAudioPaused();
  }

  private handleSocketDisconnected(): void {
    this.realtimeReady = false;
    this.clearJoinHandshakeTimeout();
    if (this.getGameState().connectionStatus === 'failed') {
      this.stopHeartbeat();
      return;
    }
    this.getGameState().setConnectionStatus('disconnected');
    clearGamingSessionOnHide('socket_disconnected');
    this.stopHeartbeat();
    pauseMusicForTransport(this.audio);
    this.onTransportAudioPaused();
  }

  private handleSocketFailed(): void {
    this.realtimeReady = false;
    this.clearJoinHandshakeTimeout();
    this.clearConnectionBootstrapTimeout();
    this.getGameState().setConnectionStatus('failed');
    clearGamingSessionOnHide('socket_failed');
    this.stopHeartbeat();
    pauseMusicForTransport(this.audio);
    this.onTransportAudioPaused();
  }

  private scheduleJoinHandshake(): void {
    this.clearJoinHandshakeTimeout();
    this.joinAckTimeout = setTimeout(() => {
      const action = resolveJoinHandshakeAction({
        connectionStatus: this.getGameState().connectionStatus,
        socketConnected: this.outbound.getAdaptor().getConnected(),
        joinRetryCount: this.joinRetryCount,
      });
      if (action === 'done') {
        return;
      }
      if (action === 'wait_for_socket') {
        this.scheduleJoinHandshake();
        return;
      }
      if (action === 'retry_join') {
        this.joinRetryCount += 1;
        console.warn(
          `[GameController] JOIN_ACK timeout, retry ${this.joinRetryCount}/${CONNECTION_CONFIG.join.maxRetries}`
        );
        this.join();
        this.scheduleJoinHandshake();
        return;
      }
      logConnection('JOIN_TIMEOUT');
      this.joinRetryCount = 0;
      this.requestReconnect(CONNECTION_FAILURE_REASON.JOIN_TIMEOUT);
    }, CONNECTION_CONFIG.join.ackTimeoutMs);
  }

  private clearJoinHandshakeTimeout(): void {
    if (this.joinAckTimeout) {
      clearTimeout(this.joinAckTimeout);
      this.joinAckTimeout = null;
    }
  }

  private markSessionConnected(): void {
    this.clearJoinHandshakeTimeout();
    this.clearConnectionBootstrapTimeout();
    this.joinRetryCount = 0;
    this.missedPongCount = 0;
    this.getGameState().setConnectionStatus('connected');
    if (!this.hasTrackedIntroClosed && !isSkyxNewRoute(this.getGameSlug())) {
      this.hasTrackedIntroClosed = true;
      track('intro_closed');
    }
    tryStartFreshGamingSession('socket_connected');
    logConnection('SYNC_COMPLETE');
    logConnection('CONNECTION_RECOVERED');
  }

  private shouldPauseWatchdogs(): boolean {
    return (
      CONNECTION_CONFIG.lifecycle.pauseWatchdogsWhenHidden &&
      typeof document !== 'undefined' &&
      document.hidden
    );
  }

  private startHeartbeat(): void {
    if (!CONNECTION_CONFIG.heartbeat.enabled) {
      return;
    }
    console.log('[Heartbeat] Starting heartbeat checks...');
    this.stopHeartbeat();
    this.waitingForPong = false;
    this.lastPingTimestamp = 0;
    this.missedPongCount = 0;
    this.sendPing();
    this.heartbeatIntervalTimer = setInterval(() => {
      if (this.shouldPauseWatchdogs()) {
        return;
      }
      if (this.waitingForPong) {
        return;
      }
      this.sendPing();
    }, CONNECTION_CONFIG.heartbeat.pingIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatIntervalTimer) {
      console.log('[Heartbeat] Stopping heartbeat checks.');
      clearInterval(this.heartbeatIntervalTimer);
      this.heartbeatIntervalTimer = null;
    }
    this.clearPongTimeout();
    this.waitingForPong = false;
    this.missedPongCount = 0;
  }

  private clearPongTimeout(): void {
    if (this.pongTimeoutTimer) {
      clearTimeout(this.pongTimeoutTimer);
      this.pongTimeoutTimer = null;
    }
  }

  private sendPing(): void {
    if (this.shouldPauseWatchdogs()) {
      return;
    }
    if (!this.outbound.getAdaptor().getConnected()) {
      console.warn('[Heartbeat] Skipping PING, not connected.');
      return;
    }
    try {
      const payload: PingPayload = {
        op: 'PING',
        clientTime: Date.now(),
        playerId: AuthService.getInstance().getPlayerId(),
      };
      const params = this.getStompPathParams();
      this.outbound.emit(STOMP_CONFIG.endpoints.ping(params), payload);
      this.waitingForPong = true;
      this.lastPingTimestamp = Date.now();
      logConnection('PING');
      this.clearPongTimeout();
      this.pongTimeoutTimer = setTimeout(() => {
        this.handleMissedPong();
      }, CONNECTION_CONFIG.heartbeat.pongTimeoutMs);
    } catch (err) {
      console.error('[Heartbeat] Failed to send PING message:', err);
      this.requestReconnect(CONNECTION_FAILURE_REASON.HEARTBEAT_TIMEOUT);
    }
  }

  private handleMissedPong(): void {
    this.pongTimeoutTimer = null;
    this.waitingForPong = false;
    const result = recordPongMiss(this.missedPongCount);
    this.missedPongCount = result.missedPongs;
    logConnection('MISSED_PONG');
    logConnection(`MISSED_PONG_COUNT: ${this.missedPongCount}`);
    if (result.unhealthy) {
      this.requestReconnect(CONNECTION_FAILURE_REASON.PONG_FAILURE);
    }
  }

  private handlePong(_pong: PongResponse): void {
    this.clearPongTimeout();
    this.waitingForPong = false;
    this.lastPingTimestamp = 0;
    this.missedPongCount = recordPongReceived().missedPongs;
    logConnection('PONG');
  }

  private checkRealtimeStale(): void {
    const now = Date.now();
    if (
      now - this.lastTickWatchdogCheckAt <
      CONNECTION_CONFIG.gameWatchdog.checkIntervalMs
    ) {
      return;
    }
    this.lastTickWatchdogCheckAt = now;
    if (
      isTickWatchdogStale({
        realtimeReady: this.realtimeReady,
        hidden: typeof document !== 'undefined' && document.hidden,
        phase: this.getGameState().phase,
        lastRealtimeEventAt: this.lastRealtimeEventAt,
        now,
      })
    ) {
      logConnection('TICK_STALE');
      this.requestReconnect(CONNECTION_FAILURE_REASON.TICK_STALE);
    }
  }

  private handleVisibilityResume(): void {
    const adaptor = this.outbound.getAdaptor();
    const action = resolveVisibilityResumeAction({
      documentHidden: typeof document !== 'undefined' && document.hidden,
      connectionStatus: this.getGameState().connectionStatus,
      socketConnected: adaptor.getConnected(),
      realtimeReady: this.realtimeReady,
    });

    if (action === 'ignore') {
      return;
    }
    if (action === 'reconnect') {
      this.requestReconnect(CONNECTION_FAILURE_REASON.VISIBILITY_RESUME);
      return;
    }
    if (action === 'rejoin') {
      console.log(
        '[GameController] Tab visible with socket up but session not ready — re-joining.'
      );
      this.joinRetryCount = 0;
      this.join();
      this.scheduleJoinHandshake();
      return;
    }

    // probe: tab freeze can make ticks look stale even when the socket is live.
    this.lastRealtimeEventAt = Date.now();
    this.clearPongTimeout();
    this.waitingForPong = false;
    this.sendPing();
  }

  /**
   * Single reconnect entry for session-level failures. Transport close already
   * schedules adaptor retry; this method shows the overlay immediately and
   * starts a fresh STOMP session when one is not already in flight.
   */
  private requestReconnect(reason: string): void {
    logConnection('CONNECTION_UNHEALTHY', reason);
    this.realtimeReady = false;
    this.stopHeartbeat();
    this.clearJoinHandshakeTimeout();
    if (this.getGameState().connectionStatus !== 'failed') {
      this.getGameState().setConnectionStatus('reconnecting');
    }
    pauseMusicForTransport(this.audio);
    this.onTransportAudioPaused();

    const adaptor = this.outbound.getAdaptor();
    if (
      !shouldDispatchReconnect({
        waitingForNetwork: Boolean(adaptor.isWaitingForNetwork?.()),
        reconnectAlreadyInProgress:
          this.reconnectRequestInProgress ||
          Boolean(adaptor.isReconnectInProgress?.()),
      })
    ) {
      return;
    }

    this.reconnectRequestInProgress = true;
    logCrashReconnect('stale connection detected', reason);
    void this.outbound
      .reconnect()
      .catch(err => {
        console.error('[GameController] Reconnect failed:', err);
      })
      .finally(() => {
        this.reconnectRequestInProgress = false;
      });
  }

  private requestStaleReconnect(reason: string): void {
    this.requestReconnect(reason);
  }

  protected onReconnectSnapshotApplied(_phase: Phase): void {
    // Game-specific visual reconstruction (plane/graph) can override.
  }

  /**
   * Called when the socket drops / reconnects and BGM is paused for transport.
   * Games can stop ambient round SFX so they do not keep playing under the
   * reconnect overlay (same quiet behavior as a mid-round same-phase rejoin).
   */
  protected onTransportAudioPaused(): void {
    // Optional game-specific ambient SFX pause.
  }

  protected getStompPathParams() {
    const state = this.getGameState() as any;
    // NOTE: gameId, tenantId, and tableId come from GAME_CONFIG via the game state
    // They are set in the game-specific state files (e.g., crash.ts, aviator.ts)
    // as: gameId: GAME_CONFIG.gameId, tenantId: GAME_CONFIG.tenantId, tableId: GAME_CONFIG.tableId
    const { gameId, tenantId, tableId } = state;
    if (!gameId || !tenantId || !tableId) {
      console.error('Missing required STOMP path parameters in game state.', {
        gameId,
        tenantId,
        tableId,
        gameSlug: state.slug || this.getGameSlug(),
      });
      throw new Error(
        'Missing required parameters for STOMP path generation (gameId, tenantId, tableId). These should come from GAME_CONFIG.'
      );
    }
    // TODO: Make this dynamic per game - currently hardcoded to 'crash' for history compatibility
    // Both crash and aviator should subscribe to the same topics for now
    // This needs to be made game-specific later when backend supports separate topics per game
    const gameSlug = 'crash'; // Temporarily hardcoded to ensure history works for both games
    const actualSlug = state.slug || this.getGameSlug();
    if (actualSlug !== 'crash') {
      console.warn(
        `[getStompPathParams] Game slug is '${actualSlug}' but using 'crash' topics for compatibility. TODO: Make topics dynamic per game.`
      );
    }
    return { gameId, gameSlug, tenantId, tableId };
  }

  protected subscribeAll(): void {
    this.unsubscribeStompTopics();
    const { topics } = STOMP_CONFIG;
    const params = this.getStompPathParams();
    const subscriptions = [
      this.outbound.on(topics.tick(params), this.handleServerMessage),
      this.outbound.on(topics.events(params), this.handleServerMessage),
      this.outbound.on(topics.pong(params), this.handleServerMessage),
      this.outbound.on(topics.ack(params), this.handleServerMessage),
      this.outbound.on(topics.error(params), this.handleServerMessage),
      this.outbound.on(topics.chatHistory(params), this.handleServerMessage),
      this.outbound.on(topics.chatMessages(params), this.handleServerMessage),
    ];
    console.log('[GameController] Subscribed to topics:', {
      tick: topics.tick(params),
      events: topics.events(params),
      pong: topics.pong(params),
      ack: topics.ack(params),
      error: topics.error(params),
      chatHistory: topics.chatHistory(params),
      chatMessages: topics.chatMessages(params),
      params,
    });
    this.stompTopicUnsubs.push(...subscriptions);
  }

  private unsubscribeStompTopics(): void {
    this.stompTopicUnsubs.forEach(unsubscribe => unsubscribe());
    this.stompTopicUnsubs = [];
  }

  protected handleErrorMessage = (errorPayload: StompError['p']): void => {
    console.error('❌ [SERVER-ERROR] Received error from server:', {
      code: (errorPayload as any).code,
      message: (errorPayload as any).msg || errorPayload.message,
      betNo: errorPayload.betNo,
      retry: (errorPayload as any).retry,
      fullPayload: errorPayload,
    });
    // Normalize all backend errors to a single user-facing message
    let message = this.getStageTimedOutMessage();
    this.getGameState().addNotification({ type: 'error', message });
    if (errorPayload.betNo) {
      let side = this.resolveCashoutSide(errorPayload.betNo);
      if (!side) {
        side = this.byBetNo(errorPayload.betNo);
      }
      console.log(
        `🔍 [SERVER-ERROR] Error for betNo ${errorPayload.betNo}, mapped to side: ${side}`
      );
      if (side) {
        const currentStatus = this.getGameState().bets[side].status;
        const currentPhase = this.getGameState().phase;
        const errorCode = (errorPayload as any).code;

        console.log(`[debugBet] handleErrorMessage processing error:`, {
          side,
          currentStatus,
          currentPhase,
          errorCode,
          message,
          betNo: errorPayload.betNo,
        });

        // Special handling for CANCEL_STAGE_TIMEOUT during BET_LOCK phase
        if (
          errorCode === 'CANCEL_STAGE_TIMEOUT' &&
          currentPhase === 'BET_LOCK'
        ) {
          console.log(
            `[debugBet] 🔒 CANCEL_STAGE_TIMEOUT during BET_LOCK - keeping bet locked, not changing status`
          );
          // Don't change the bet status during BET_LOCK phase when cancel times out
          // The bet should remain 'placed' and locked in
          this.getGameState().upBet(side, {
            error: message,
          });
          return;
        }

        // Special handling for STAGE_TIMEOUT during BET_LOCK phase (bet placement rejected)
        if (errorCode === 'STAGE_TIMEOUT' && currentPhase === 'BET_LOCK') {
          console.log(
            `[debugBet] 🔒 STAGE_TIMEOUT during BET_LOCK - bet placement rejected, reverting to idle`
          );
          // Bet placement was rejected due to stage timeout, revert to idle
          this.getGameState().upBet(side, {
            status: 'idle',
            error: message,
            serverBetId: undefined,
            betNo: undefined,
          });
          return;
        }

        let newStatus: BetBoxState['status'] = 'idle'; // Default to idle for most errors
        if (currentStatus === 'cashing') {
          this.applyCashoutFailure(side, message, errorPayload.betNo);
          return;
        } else if (currentStatus === 'placing') {
          newStatus = 'idle'; // If placing failed, it's 'idle'
        } else if (currentStatus === 'placed') {
          newStatus = 'placed'; // If already placed, keep it 'placed'
        } else if (currentStatus === 'armed') {
          newStatus = 'armed'; // Keep it armed
        }

        console.log(`[debugBet] handleErrorMessage changing status:`, {
          side,
          from: currentStatus,
          to: newStatus,
          reason: 'server error',
        });

        this.getGameState().upBet(side, {
          status: newStatus,
          error: message,
        });

        console.log(
          `🔄 [SERVER-ERROR] Set side ${side} to ${newStatus} due to error (was ${currentStatus})`
        );
      } else {
        const cashingSide = Object.keys(this.getGameState().bets).find(
          s => this.getGameState().bets[s as BetSide].status === 'cashing'
        ) as BetSide | undefined;
        if (cashingSide && this.isRoundEndedPhase(this.getGameState().phase)) {
          this.applyCashoutFailure(cashingSide, message, errorPayload.betNo);
          return;
        }
        console.error(
          `❌ [SERVER-ERROR] Could not find side for betNo: ${errorPayload.betNo}`
        );
      }
    } else {
      const cashingSide = Object.keys(this.getGameState().bets).find(
        s => this.getGameState().bets[s as BetSide].status === 'cashing'
      ) as BetSide | undefined;
      if (cashingSide && this.isRoundEndedPhase(this.getGameState().phase)) {
        this.applyCashoutFailure(cashingSide, message);
        return;
      }
      this.getGameState().setLastMessage(`ERROR: ${message}`);
    }
  };

  private parsePositiveRoundNo(value: unknown): number | null {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
  }

  private setCurrentRoundNo(roundNo: number): void {
    const parsed = this.parsePositiveRoundNo(roundNo);
    if (parsed == null) return;
    const state = this.getGameState();
    if (typeof state.setRoundNo !== 'function') return;
    if (state.roundNo === parsed) return;
    // Sequential round numbers only move forward. Ignore stale ROUND_SUMMARY
    // / ROUND_UPDATE payloads that arrive after the next round has started.
    if (typeof state.roundNo === 'number' && parsed < state.roundNo) return;
    state.setRoundNo(parsed);
  }

  private handleRoundUpdate = (payload: StompRoundUpdate): void => {
    const body = payload?.p ?? {};
    const roundNo = this.parsePositiveRoundNo(body.roundNo);
    const updateRoundId =
      body.roundId != null && String(body.roundId).length > 0
        ? String(body.roundId)
        : null;

    console.log('[GameController] Received ROUND_UPDATE:', {
      roundNo,
      roundId: updateRoundId,
    });

    if (roundNo == null) return;

    const state = this.getGameState();
    const liveRoundId =
      state.roundId != null && String(state.roundId).length > 0
        ? String(state.roundId)
        : null;
    const phase = String(state.phase ?? '');
    const nextRoundAlreadyStarted =
      (phase === 'WAITING' || phase === 'LOBBY') &&
      liveRoundId != null &&
      updateRoundId != null &&
      liveRoundId !== updateRoundId;

    this.setCurrentRoundNo(nextRoundAlreadyStarted ? roundNo + 1 : roundNo);
  };

  private handleChatHistory = (payload: any): void => {
    if (payload && payload.messages && Array.isArray(payload.messages)) {
      GlobalBus.emit('chat:history', payload);
    } else {
      GlobalBus.emit('chat:new_message', payload);
    }
  };

  private handleChatMessage = (payload: any): void => {
    GlobalBus.emit('chat:new_message', payload);
  };

  private handleChatLikeUpdate = (payload: any): void => {
    const clientSeq =
      payload?.content?.clientSeq ??
      payload?.meta?.clientSeq ??
      payload?.clientSeq ??
      payload?.clientId;
    if (!clientSeq) return;

    const likeCount =
      payload?.content?.likeCount ??
      payload?.content?.likes ??
      payload?.meta?.likeCount ??
      payload?.meta?.likes ??
      payload?.likeCount ??
      payload?.likes;
    const likedByMe =
      payload?.content?.likedByMe ??
      payload?.content?.liked ??
      payload?.meta?.likedByMe ??
      payload?.meta?.liked ??
      payload?.likedByMe ??
      payload?.liked;

    GlobalBus.emit('chat:like_update', {
      clientSeq,
      likeCount: typeof likeCount === 'number' ? likeCount : undefined,
      likedByMe: typeof likedByMe === 'boolean' ? likedByMe : undefined,
    });
  };

  /** skyx_new: chat/subscribe is @SubscribeMapping — use STOMP SUBSCRIBE, not SEND. */
  protected shouldUseStompSubscribeForChat(): boolean {
    return false;
  }

  public subscribeToChat(): void {
    try {
      const params = this.getStompPathParams();
      const endpoint = STOMP_CONFIG.endpoints.chatSubscribe(params);

      if (this.shouldUseStompSubscribeForChat()) {
        // stompjs subscribe() throws synchronously if the socket is not up yet.
        if (!this.outbound.getAdaptor()?.getConnected()) {
          return;
        }

        if (this.chatAppSubscribeUnsub) {
          this.chatAppSubscribeUnsub();
          this.chatAppSubscribeUnsub = null;
        }

        const destination = `${STOMP_CONFIG.prefixes.app}/${endpoint}`;
        console.log(
          '[GameController] STOMP SUBSCRIBE chat snapshot:',
          destination
        );

        this.chatAppSubscribeUnsub = this.outbound.on(destination, payload => {
          console.log(
            '[GameController] Chat snapshot received via app subscribe'
          );
          this.handleChatHistory(payload);
          if (this.chatAppSubscribeUnsub) {
            this.chatAppSubscribeUnsub();
            this.chatAppSubscribeUnsub = null;
          }
        });
        return;
      }

      this.outbound.emit(endpoint, {});
    } catch (err) {
      console.warn('[BaseGameController] Failed to subscribe to chat:', err);
    }
  }

  public sendChatMessage(text: string): string {
    const normalized = normalizeOutgoingChatMessage(text);
    if (!normalized) return '';

    const clientId = `c-${Math.random().toString(36).substring(2, 9)}`;
    try {
      const params = this.getStompPathParams();
      const payload = {
        text: normalized,
        clientId: clientId,
      };
      const destination = STOMP_CONFIG.endpoints.chatSend(params);
      this.outbound.emit(destination, payload);
    } catch (err) {
      console.error('[BaseGameController] Failed to send chat message:', err);
    }
    return clientId;
  }

  public sendChatLike(clientSeq: string): void {
    try {
      const params = this.getStompPathParams();
      const destination = STOMP_CONFIG.endpoints.chatLike(params);
      this.outbound.emit(destination, { clientSeq });
    } catch (err) {
      console.error('[BaseGameController] Failed to send chat like:', err);
    }
  }

  private handleJoinAck(ack: StompJoinAck): void {
    try {
      logCrashReconnect('JOIN snapshot received');
      logConnection('JOIN_ACK');
      logConnection('SNAPSHOT_RECEIVED');
      this.syncPlayerNicknameFromJoinAck(ack);

      const payload = (ack.p ?? ack) as Record<string, unknown>;
      const clientPhaseGuess = mapServerPhaseToClient(payload.phase);
      const defaultDuration =
        PHASE_CONFIG[clientPhaseGuess]?.defaultDuration ??
        PHASE_CONFIG.WAITING.defaultDuration;
      const snapshot = parseJoinSnapshot(payload, defaultDuration);
      if (!snapshot) {
        throw new Error('JOIN snapshot missing payload');
      }

      const params = this.getStompPathParams();
      if (
        !validateSnapshotIdentity(snapshot, {
          tenantId: params.tenantId,
          gameId: params.gameId,
          tableId: params.tableId,
        })
      ) {
        logCrashReconnect('JOIN snapshot ignored — table identity mismatch', {
          expected: params,
          received: {
            tenantId: snapshot.tenantId,
            gameId: snapshot.gameId,
            tableId: snapshot.tableId,
          },
        });
        return;
      }

      logCrashReconnect('applying snapshot', {
        phase: snapshot.serverPhase,
        clientPhase: snapshot.clientPhase,
        roundId: snapshot.roundId,
        multiplier: snapshot.multiplier,
      });

      const gameState = this.getGameState();
      if (snapshot.roundId) {
        gameState.setRound(snapshot.roundId);
      }
      if (snapshot.roundNo != null) {
        this.setCurrentRoundNo(snapshot.roundNo);
      }
      if (snapshot.multiplier != null) {
        gameState.setTick(
          snapshot.multiplier,
          snapshot.serverNow ?? Date.now()
        );
      }
      gameState.setPhaseDurations(
        snapshot.totalDuration,
        snapshot.remainingDuration
      );
      if (snapshot.currency) {
        gameState.setCurrency(snapshot.currency);
      }
      if (snapshot.balance != null) {
        // Mid-round reconnect: stash net delta, apply only on FLEW_AWAY settle.
        const phaseForReconcile = snapshot.clientPhase ?? gameState.phase ?? '';
        const roundInProgress =
          phaseForReconcile === 'BET_LOCK' ||
          phaseForReconcile === 'TAKEOFF' ||
          phaseForReconcile === 'FLYING';
        reconcileReconnectBalance(snapshot.balance, { roundInProgress });
        gameState.setBalance(snapshot.balance);
      }

      const currentPhase = gameState.phase;
      // Re-entering WAITING would resetForNextRound() and re-place armed bets.
      if (currentPhase !== snapshot.clientPhase) {
        this.transitionToSnapshotPhase(snapshot);
      } else {
        logCrashReconnect('keeping current phase', { phase: currentPhase });
      }
      applyReconnectSnapshot(
        this.getGameStateStore(),
        snapshot,
        this.getBetSides()
      );
      // Server auto-cashout may already be CASHED_OUT on JOIN with no CASHOUT ACK.
      this.syncGamingSessionCashedBetsFromReconnect();
      // Ignore late CASHOUT ACKs for bets already restored as cashed (avoids
      // double-counting wins into deferred reconnect net).
      for (const side of this.getBetSides()) {
        const betNo = this.getGameState().bets[side]?.betNo;
        if (
          this.getGameState().bets[side]?.status === 'cashed' &&
          betNo != null
        ) {
          this.processedCashoutAckBetNos.add(betNo);
        }
      }
      this.onReconnectSnapshotApplied(snapshot.clientPhase);

      this.realtimeReady = true;
      this.lastRealtimeEventAt = Date.now();
      logCrashReconnect('snapshot applied');
      logCrashReconnect('realtime processing resumed');
      this.markSessionConnected();
    } catch (error) {
      console.error('[JoinAck] Failed to restore session after reload:', error);
      this.realtimeReady = false;
      this.requestReconnect(CONNECTION_FAILURE_REASON.JOIN_TIMEOUT);
      return;
    }

    try {
      this.subscribeToChat();
    } catch (error) {
      console.warn('[JoinAck] Failed to subscribe to chat:', error);
    }

    restoreMusicAfterTransport(this.audio);
    this.isBgMusicPlaying =
      Boolean(this.audio) &&
      !this.audio.isMusicMuted() &&
      this.audio.isMusicPlaying();
  }

  private transitionToSnapshotPhase(snapshot: CrashReconnectSnapshot): void {
    const context = this.phaseManager['context'];
    const timingData = {
      phase: snapshot.serverPhase ?? snapshot.clientPhase,
      phaseStart: snapshot.phaseStart ?? Date.now(),
      phaseEnd: snapshot.phaseEnd ?? Date.now(),
    };
    const phasePayload = {
      roundId: snapshot.roundNo ?? (Number(snapshot.roundId) || 0),
      phase: (snapshot.serverPhase ??
        snapshot.clientPhase) as StompPhaseUpdate['p']['phase'],
      phaseStart: snapshot.phaseStart ?? Date.now(),
      phaseEnd: snapshot.phaseEnd ?? Date.now(),
      bust: snapshot.bust,
    };

    switch (snapshot.clientPhase) {
      case 'WAITING':
        this.clearProcessedCashoutAcks();
        this.phaseManager.transitionTo(new WaitingPhase(context), timingData);
        break;
      case 'BET_LOCK':
        this.phaseManager.transitionTo(new BetLockPhase(context), timingData);
        break;
      case 'TAKEOFF':
        this.phaseManager.transitionTo(new TakeOffPhase(context), timingData);
        break;
      case 'FLYING':
        this.phaseManager.transitionTo(new FlyingPhase(context), timingData);
        break;
      case 'FLEW_AWAY':
        this.phaseManager.transitionTo(
          new FlewAwayPhase(context, phasePayload, snapshot.remainingDuration),
          timingData
        );
        break;
      case 'REVEAL':
        this.phaseManager.transitionTo(
          new RevealPhase(context, phasePayload),
          timingData
        );
        break;
      default:
        console.warn(
          `[JoinAck] Unknown phase received on join: ${snapshot.serverPhase}`
        );
        this.phaseManager.transitionTo(new WaitingPhase(context), timingData);
    }
  }

  private handleAck(ack: StompGenericAck): void {
    const { code, data } = ack.p;
    const result = data.result;
    const betNo = result?.betNo;

    let totalBalance = NaN;
    if (data.balance) {
      if (typeof data.balance.total === 'number') {
        totalBalance = data.balance.total;
      } else if (typeof data.balance === 'number') {
        totalBalance = data.balance;
      }
      if (!isNaN(totalBalance)) {
        this.getGameState().setBalance(totalBalance);
      }
    }

    const roundHistory = result?.roundState?.roundHistory;
    if (roundHistory && Array.isArray(roundHistory)) {
      const historyItems = roundHistory
        .filter((item: any) => item.roundNo !== null)
        .map((item: any, index: number) => ({
          roundId: item.roundNo || `R${index}`,
          bust: item.crashX || 1.0,
          serverSeed: item.serverSeed || 'N/A',
          serverSeedHash: item.serverSeedHash || 'N/A',
        }))
        .slice(0, 60);

      if (historyItems.length > 0) {
        this.getGameState().history = historyItems;
        this.getGameState().setConnectionStatus('connected');
      }
    }

    const side = this.resolveCashoutSide(betNo);
    const payoutRaw = result?.payoutChips ?? result?.payout;
    const isCashoutResult =
      code === 'OK' &&
      result?.atX != null &&
      payoutRaw != null &&
      !Number.isNaN(parseFloat(String(result.atX))) &&
      !Number.isNaN(parseFloat(String(payoutRaw)));

    if (isCashoutResult) {
      const cashoutMultiplier = parseFloat(String(result.atX));
      const winAmount = formatCentsToCurrency(parseFloat(String(payoutRaw)));
      const newBalance = !isNaN(totalBalance)
        ? totalBalance
        : this.getGameState().balance;

      this.finalizeCashoutAck(
        betNo,
        side,
        cashoutMultiplier,
        winAmount,
        newBalance
      );
      return;
    }

    if (!side) {
      if (!betNo && code === 'OK') {
        return;
      }
      return;
    }

    if (code === 'OK' && result) {
      if (result.betNo && (result.status === 'OK' || result.stakeChips)) {
        // const betState = this.getGameState().bets[side];
        // if (betState.status !== 'placed') {
        //   this.getGameState().upBet(side, {
        //     status: 'placed',
        //     placedAtX: this.getGameState().nowX,
        //     error: '', // Clear error on success
        //   });
        // }
      }
    } else {
      if (betNo != null) {
        this.pendingCashouts.delete(betNo);
      }
      const message = data.message || 'An unknown error occurred';
      this.getGameState().addNotification({
        type: 'error',
        message: message,
      });

      const currentBet = this.getGameState().bets[side];
      if (currentBet.status === 'placing') {
        this.getGameState().upBet(side, { status: 'rejected', error: message });
        setTimeout(() => {
          this.getGameState().upBet(side, { status: 'idle', error: '' });
        }, 2000);
      } else if (currentBet.status === 'cashing') {
        this.applyCashoutFailure(side, message, betNo);
      } else {
        // Just show the error for any other state
        this.getGameState().upBet(side, { error: message });
      }
    }
  }

  /**
   * Resolve the outbound PF client seed for the current player + round/game.
   * Regenerates when the round (or player) context changes.
   */
  protected resolveClientSeed(forceRoundId?: string): string {
    const playerId = AuthService.getInstance().getPlayerId() ?? 'anonymous';
    const state = this.getGameState() as { roundId?: string | number };
    const roundId =
      forceRoundId ??
      (state.roundId != null && String(state.roundId).length > 0
        ? String(state.roundId)
        : null);
    const gameOrRoundId =
      roundId ?? String(this.getStompPathParams().gameId ?? 'game');
    return ClientSeedService.getInstance().ensureClientSeed(
      playerId,
      gameOrRoundId
    );
  }

  protected join(): void {
    try {
      const requestId = `join-req-${Date.now()}`;
      const payload = {
        v: '1',
        op: 'JOIN',
        id: requestId,
        p: {
          clientSeed: this.resolveClientSeed(),
          clientNonce: 1,
          idem: `join-${requestId}`,
        },
      };
      const params = this.getStompPathParams();
      this.outbound.emit(STOMP_CONFIG.endpoints.joinGame(params), payload);
      logConnection('JOIN', {
        generation: this.activeConnectionGeneration,
      });
      logCrashReconnect('JOIN sent', {
        generation: this.activeConnectionGeneration,
      });
    } catch (err) {
      console.error('[BaseGameController] Failed to send join message:', err);
      this.getGameState().setLastMessage(
        'JOIN ERROR: ' + (err as Error).message
      );
    }
  }

  resolveBetAction(side: BetSide): void {
    const gameState = this.getGameState();
    const bet = gameState.bets[side];
    if (
      bet?.status !== 'idle' &&
      bet?.status !== 'cashed' &&
      bet?.status !== 'lost' &&
      bet?.status !== 'armed'
    ) {
      return;
    }

    const validationResult = this.validatePlaceBet(side, gameState, bet);
    if (!validationResult.isValid) {
      gameState.upBet(side, {
        error: validationResult.error,
        status: 'rejected',
      });
      setTimeout(() => {
        gameState.upBet(side, { error: '', status: 'idle' });
      }, 3000);
      return;
    }

    if (
      gameState.phase === 'FLYING' ||
      gameState.phase === 'FLEW_AWAY' ||
      gameState.phase === 'REVEAL'
    ) {
      const stake = bet.stake;
      if (bet.betMode !== 'auto') {
        track('spin_clicked', { bet_amount: stake, side });
      }
      if (bet?.status !== 'armed' && this.audio) {
        this.playBetActionSound();
      }

      gameState.upBet(side, {
        status: 'armed',
        cashedAtX: undefined,
        win: undefined,
        error: '',
        balanceDeducted: stake > 0, // Keep this to track intent
      });

      if (this.audio) {
        this.playBetActionSound();
      }
    } else if (gameState.phase === 'WAITING') {
      this.place(side);
    }
  }

  private translateGameMessage(key: string, defaultValue: string): string {
    if (isSkyxNewRoute(this.getGameSlug())) {
      return i18next.t(key, {
        ns: [SKYX_NEW_NS, 'common'],
        defaultValue,
      });
    }
    return i18next.t(key, { ns: 'common', defaultValue });
  }

  private getStageTimedOutMessage(): string {
    return this.translateGameMessage(
      'ui.messages.error_stage_timed_out',
      'Stage Timed Out'
    );
  }

  protected isRoundEndedPhase(phase: Phase): boolean {
    return (
      phase === 'FLEW_AWAY' ||
      phase === 'REVEAL' ||
      phase === 'CRASHED' ||
      phase === 'WAITING'
    );
  }

  protected resolveStatusAfterCashoutFailure(): BetBoxState['status'] {
    if (!this.isRoundEndedPhase(this.getGameState().phase)) {
      return 'placed';
    }
    return 'idle';
  }

  protected applyCashoutFailure(
    side: BetSide,
    message: string,
    betNo?: number
  ): void {
    const gameState = this.getGameState();
    const currentStatus = gameState.bets[side]?.status;
    if (currentStatus !== 'cashing' && currentStatus !== 'placed') {
      if (betNo != null) {
        this.pendingCashouts.delete(betNo);
      }
      return;
    }

    const newStatus = this.resolveStatusAfterCashoutFailure();
    if (betNo != null) {
      this.pendingCashouts.delete(betNo);
    } else if (gameState.bets[side].betNo != null) {
      this.pendingCashouts.delete(gameState.bets[side].betNo as number);
    }

    const patch: Partial<BetBoxState> = {
      status: newStatus,
      error: newStatus === 'idle' ? '' : message,
    };
    if (newStatus === 'idle') {
      patch.serverBetId = undefined;
      patch.betNo = undefined;
    }

    gameState.upBet(side, patch);
  }

  private getInsufficientBalanceMessage(): string {
    return this.translateGameMessage(
      'ui.messages.error_insufficient_balance',
      'Insufficient balance'
    );
  }

  private validatePlaceBet(
    side: BetSide,
    gameState: any,
    bet: any
  ): { isValid: boolean; error?: string } {
    if (!bet.stake || bet.stake <= 0) {
      return { isValid: false, error: 'Invalid bet amount' };
    }
    const { minBet, maxBet } = this.getBetLimits();
    if (bet.stake < minBet) {
      return { isValid: false, error: `Minimum bet is ${minBet}` };
    }
    if (bet.stake > maxBet) {
      return { isValid: false, error: `Maximum bet is ${maxBet}` };
    }
    if (gameState.balance < bet.stake) {
      const insufficientBalanceMessage = this.getInsufficientBalanceMessage();
      this.getGameState().addNotification({
        type: 'error',
        message: insufficientBalanceMessage,
      });
      return { isValid: false, error: insufficientBalanceMessage };
    }
    const validPhases = ['WAITING', 'FLYING', 'FLEW_AWAY', 'REVEAL'];
    if (!validPhases.includes(gameState.phase)) {
      return {
        isValid: false,
        error: 'Cannot place bet in current game phase',
      };
    }
    return { isValid: true };
  }

  place(side: BetSide): void {
    const betState = this.getGameState().bets[side];
    if (!betState || betState.betNo) {
      return;
    }
    const stake = betState.stake;
    const isFromArmedState = betState.status === 'armed';

    this.getGameState().upBet(side, {
      status: 'placing',
      error: '',
      balanceDeducted: stake > 0, // Keep this to track intent
    });

    // Play bet sound for manual clicks (not from armed/autoplay state)
    if (!isFromArmedState && this.audio) {
      this.playBetActionSound();
    }
    // --- END MODIFIED ---

    const assignedBetId = this.getGameState().assignBetId(side);

    if (assignedBetId === 0) {
      this.getGameState().upBet(side, {
        status: 'idle',
        error: 'No bet slots available.',
        balanceDeducted: false,
      });
      return;
    }

    try {
      const requestId = `r-${Math.random().toString(36).substring(7)}`;
      const currentBet = this.getGameState().bets[side];
      const payloadParams: PlaceBetPayload['p'] = {
        stakeChips: Math.round(currentBet.stake * 100),
        betId: assignedBetId,
      };

      // Conditionally add autoCashoutX to the payload if enabled.
      // The server will handle the cashout automatically.
      if (currentBet.autoCashoutEnabled && currentBet.autoCashoutX) {
        payloadParams.autoCashoutX = currentBet.autoCashoutX;
        console.log(
          `📝 [BET-NOTE] Auto-cashout enabled, sending autoCashoutX: ${payloadParams.autoCashoutX}`
        );
      } else {
        console.log(
          `📝 [BET-NOTE] Auto-cashout is disabled. Not sending autoCashoutX.`
        );
      }

      const payload: PlaceBetPayload = {
        v: '1',
        op: 'PLACE_BET',
        id: requestId,
        p: payloadParams,
        idem: `bet-${assignedBetId}-${requestId}`,
        clientSeed: this.resolveClientSeed(),
        clientNonce: 1,
      };

      const params = this.getStompPathParams();
      const endpoint = STOMP_CONFIG.endpoints.placeBet(params);
      this.outbound.emit(endpoint, payload);
      track('bet_placed', {
        bet_amount: currentBet.stake,
        side,
      });
      if (!isFromArmedState && currentBet.betMode !== 'auto') {
        track('spin_clicked', {
          bet_amount: currentBet.stake,
          side,
        });
      }
    } catch (err) {
      this.getGameState().upBet(side, {
        status: 'idle',
        error: (err as Error).message,
      });
    }
  }

  cashout(side: BetSide): void {
    const gameState = this.getGameState();
    const bet = gameState.bets[side];
    const phase = gameState.phase;

    if (
      bet?.status !== 'placed' ||
      !bet.betNo ||
      phase === 'REVEAL' ||
      phase === 'FLEW_AWAY'
    ) {
      return;
    }

    gameState.upBet(side, { status: 'cashing' });
    this.pendingCashouts.set(bet.betNo, side);

    try {
      const requestId = `r-${Math.random().toString(36).substring(7)}`;

      const payload: CashoutPayload = {
        v: '1',
        op: 'CASHOUT_NOW',
        id: requestId,
        p: { betId: bet.betNo, expectAt: gameState.nowX },
        idem: `cash-${bet.betNo}-${requestId}`,
      };

      const params = this.getStompPathParams();
      const endpoint = STOMP_CONFIG.endpoints.cashout(params);

      // Debug logging for cashout request
      console.log('🚀 [current-debug] CASHOUT REQUEST SENT:', {
        payload,
        currentX: gameState.nowX,
        betNo: bet.betNo,
        side,
        endpoint,
        timestamp: Date.now(),
      });

      this.outbound.emit(endpoint, payload);
    } catch (err) {
      this.pendingCashouts.delete(bet.betNo);
      // If sending fails, revert to 'placed' and show error
      gameState.upBet(side, {
        status: 'placed',
        error: (err as Error).message,
      });
    }
  }

  cancelBet(side: BetSide): void {
    const gameState = this.getGameState();
    const bet = gameState.bets[side];
    const currentPhase = gameState.phase;

    console.log(`[debugBet] cancelBet called for side ${side}:`, {
      currentStatus: bet?.status,
      currentPhase,
      betNo: bet?.betNo,
      stake: bet?.stake,
      timestamp: Date.now(),
    });

    const isCancellable =
      bet?.status === 'armed' ||
      bet?.status === 'placing' ||
      (bet?.status === 'placed' && gameState.phase === 'WAITING');

    console.log(`[debugBet] isCancellable check:`, {
      isCancellable,
      status: bet?.status,
      phase: gameState.phase,
      condition: `status=${bet?.status}, phase=${gameState.phase}`,
    });

    if (!isCancellable) {
      console.log(`[debugBet] cancelBet blocked - not cancellable`);
      return;
    }

    // Play bet sound for manual cancel clicks
    if (this.audio) {
      this.playBetActionSound();
    }

    const betNo = bet.betNo;

    // For 'armed' bets, we can cancel immediately since they haven't been sent to server yet
    if (bet.status === 'armed') {
      console.log(`[debugBet] Cancelling armed bet - changing to idle`);
      gameState.upBet(side, {
        status: 'idle',
        error: '',
        serverBetId: undefined,
        betNo: undefined,
      });
      return;
    }

    // For 'placed' or 'placing' bets, only cancel if we're in WAITING phase
    // During BET_LOCK phase, don't allow cancellation
    if ((bet.status === 'placed' || bet.status === 'placing') && betNo) {
      if (gameState.phase === 'WAITING') {
        console.log(
          `[debugBet] Sending cancel request to server for betNo ${betNo} - NOT changing status yet`
        );
        // Set status to 'cancelling' to show user that cancel is in progress
        gameState.upBet(side, {
          status: 'cancelling',
          error: '',
        });

        try {
          const payload: CancelBetPayload = {
            v: '1',
            op: 'CANCEL_BET',
            id: `r-${Math.random().toString(36).substring(7)}`,
            p: { betId: betNo },
          };
          const params = this.getStompPathParams();
          this.outbound.emit(STOMP_CONFIG.endpoints.cancelBet(params), payload);
          console.log(
            `[debugBet] Cancel request sent to server for betNo ${betNo}`
          );

          // Set a timeout to revert to 'placed' if no response received within 5 seconds
          setTimeout(() => {
            const currentBet = this.getGameState().bets[side];
            if (currentBet.status === 'cancelling') {
              console.log(
                `[debugBet] Cancel request timeout - reverting side ${side} to placed`
              );
              this.getGameState().upBet(side, {
                status: 'placed',
                serverBetId: bet.serverBetId,
                betNo: betNo,
              });
            }
          }, 5000);
        } catch (err) {
          console.log(
            `[debugBet] Cancel request failed, reverting to placed:`,
            err
          );
          // If sending fails, revert to 'placed'
          gameState.upBet(side, {
            status: 'placed',
            serverBetId: bet.serverBetId,
            betNo: betNo,
          });
        }
      } else {
        // During BET_LOCK or other phases, don't allow cancellation
        console.log(
          `[debugBet] 🔒 Cannot cancel bet during ${gameState.phase} phase - bet remains locked`
        );
        // Suppress noisy popup here; do not show a user-facing error
      }
    }
  }

  protected byBetNo(betNo?: number): BetSide | undefined {
    if (!betNo) return undefined;
    const bets = this.getGameState().bets;
    return Object.keys(bets).find(s => bets[s as BetSide].betNo === betNo) as
      | BetSide
      | undefined;
  }

  /**
   * Resolve which bet panel a cashout ACK belongs to.
   * Falls back to `pendingCashouts` when FLEW_AWAY already wiped `betNo`.
   */
  protected resolveCashoutSide(betNo?: number): BetSide | undefined {
    const fromSlot = this.byBetNo(betNo);
    if (fromSlot) return fromSlot;
    if (betNo == null) return undefined;
    return this.pendingCashouts.get(betNo);
  }

  setAutoX(side: BetSide, autoCashoutX: number): void {
    this.getGameState().upBet(side, { autoCashoutX });
  }

  private countActivePlayersFromEntries(entries: any[]): number {
    const activePlayerNicknames = new Set<string>();

    for (const entry of entries) {
      const nickname =
        typeof entry?.nickname === 'string' ? entry.nickname.trim() : '';
      if (nickname && entry?.status === 'PLACED') {
        activePlayerNicknames.add(nickname);
      }
    }

    return activePlayerNicknames.size;
  }

  private handleAllActiveRounds(payload: any): void {
    const newEntries = payload.entries || [];
    const currentBets = this.getGameState().allBets || [];

    if (
      newEntries.length === 0 &&
      currentBets.length > 1 &&
      this.getGameState().phase === 'WAITING'
    ) {
      console.warn(
        '[GameController] Ignoring suspect empty ALL_ACTIVE_ROUNDS during WAITING phase.'
      );
      return;
    }

    this.getGameState().setServerBets(newEntries);
    this.getGameState().setRoundStats({
      activePlayers: this.countActivePlayersFromEntries(newEntries),
    });
    this.syncPlayerNicknameFromActiveBets(newEntries);
  }

  /** sky-x new: learn Player-xxxx from JOIN_ACK as soon as the table connection is ready. */
  private syncPlayerNicknameFromJoinAck(ack: StompJoinAck): void {
    if (!isSkyxNewRoute(this.getGameSlug())) {
      return;
    }

    if (applyServerNicknameFromPayload(ack)) {
      return;
    }

    applyServerNicknameFromPayload(ack.p);
  }

  /** sky-x new: learn Player-xxxx from the current user's row in ALL_ACTIVE_ROUNDS. */
  private syncPlayerNicknameFromActiveBets(entries: any[]): void {
    if (!isSkyxNewRoute(this.getGameSlug()) || !Array.isArray(entries)) {
      return;
    }

    const playerId = AuthService.getInstance().getPlayerId();
    if (playerId) {
      for (const entry of entries) {
        const entryPlayerId = String(
          entry?.playerId ?? entry?.player_id ?? ''
        ).trim();
        if (entryPlayerId && entryPlayerId === playerId) {
          if (applyServerNicknameFromPayload(entry)) {
            return;
          }
        }
      }
    }
    const gameState = this.getGameState() as {
      bets: Record<BetSide, BetBoxState>;
    };
    const myBetNos = this.getBetSides()
      .map(side => gameState.bets?.[side]?.betNo)
      .filter(
        (betNo): betNo is number => typeof betNo === 'number' && betNo > 0
      );

    if (myBetNos.length === 0) {
      return;
    }

    for (const entry of entries) {
      const betNo = Number(entry?.betId ?? entry?.betNo ?? entry?.id);
      if (!myBetNos.includes(betNo)) {
        continue;
      }
      if (applyServerNicknameFromPayload(entry)) {
        return;
      }
    }
  }

  private handleActiveBetPlayers(payload: any): void {
    this.getGameState().setRoundStats({ activePlayers: payload.activePlayers });
  }

  private handleTotalCashoutAmount(payload: any): void {
    this.getGameState().setRoundStats({
      totalCashout: formatCentsToCurrency(payload.totalCashout),
    });
  }

  private handleActiveVsTotalBetsCount(payload: any): void {
    this.getGameState().setRoundStats({
      activeBets: payload.active,
      totalBets: payload.total,
    });
  }
}
